import { Fragment, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api, ApiError } from '../api/client'
import { InstallaAgente } from '../components/InstallaAgente'
import { SezioneCertificati, type CertificatoTLS } from '../components/CertificatiTLS'
import { TestoConKev, cveDaProva } from '../components/Kev'
import { attributeLabel, attributeValue, scalar } from '../attributes'
import { reteNascosta } from '../util/rete'
import { campiRiassunti, diffLista, nomeElemento, type DiffLista } from '../diffListe'
import type { JobCreate,
  AssetDraft,
  NetworkInterface,
  FirewallAbbinamento,
  FirewallSyncEsito,
  CIChange,
  CIDetail as CIDetailType,
  CIRelation,
  ConfigBackupStatus,
  AssessmentResult,
  SecurityFinding,
  FirewallPeer,
  RouteEntry,
  VeeamObject,
  WlanAccessPoint,
  WlanVap,
  WlanClientEntry,
  IpamAddress,
  DeviceComponent,
  ProvenanceSource,
  ProvenanceJob,
  ProvenanceAttribute,
  PublicIp,
  HostSighting,
  ExtraAddress,
  ExtraFqdn,
  NtopngTalker,
  WazuhAlert,
  WazuhVuln,
  WazuhSca,
} from '../api/types'
import { PageSection,
  SectionedPage,
  Card,
  EmptyState,
  ErrorBanner,
  formatDateTime,
  LifecycleBadge,
  RoleBadge,
  Spinner,
  StatCard,
  StatusBadge,
} from '../components/common'
import { SchedaCampiPersonalizzati } from '../components/customFields'
import { StoriaConfigurazione } from '../components/StoriaConfigurazione'
import { EditorTag } from '../components/Tag'
import { AnagraficaIndirizzo } from '../components/AnagraficaIndirizzo'
import Marchio from '../components/Marchio'
import { riassuntoFilo } from '../components/ElencoHostFilo'
import { SchedaCluster } from '../components/SchedaCluster'
import { FinestraPortaMac } from '../components/FinestraPortaMac'
import { CategoryBars, Donut, TrafficBars, formatBytes } from '../components/charts'
import type { Category } from '../components/charts'
import type { DonutSlice } from '../components/charts'
import { DataTable } from '../components/DataTable'
import type { Column } from '../components/DataTable'
import { FirewallFlowsTable } from '../components/FirewallFlows'
import { nomePortaCanonico, stessaPorta } from '../components/interfacce'
import { useAsync } from '../hooks/useAsync'
import { usePermessiModuli } from '../auth/permessi'
import { SezionePatchingCI } from '../components/SezionePatchingCI'
import { SezioneEagle } from '../components/SezioneEagle'
import { useEagle } from '../hooks/useEagle'
import { useMed, useStatoMed } from '../hooks/useMed'
import { MappaClinica } from '../components/med/MappaClinica'
import { localeCorrente, t } from '../i18n/lingua'
import { azioniDa } from '../security/azioni'
import { ContrattiDelCi } from '../components/ContrattiDelCi'
import { SezioneServiziCI } from '../components/ServiziDiBusiness'
import { SezioneSbomCI } from '../components/Sbom'
import { SezioneRuntimeCI } from '../components/Runtime'
import { SezioneContainerCI } from '../components/Container'

/**
 * Attributi mostrati in evidenza, nell'ordine in cui hanno senso leggerli.
 *
 * I nomi sono quelli **canonici** del vocabolario del Control Plane
 * (`app/services/model/attributes.py`), non quelli che una sonda usa sul filo.
 * Qui c'erano `sysDescr`, `sysName`, `sysLocation`, `sysContact`: nomi che
 * l'ingest traduce e che quindi in archivio non esistono più. Il risultato era
 * che un CI riscansionato dopo l'ingresso di quegli attributi nel vocabolario
 * mostrava un'anagrafica **senza quei quattro campi**, mentre uno più vecchio,
 * che si portava dietro i nomi grezzi, li mostrava tutti.
 *
 * Due apparati identici, uno con la scheda piena e uno con quattro righe
 * mancanti. Il dato c'era in entrambi, sotto un altro nome: si leggeva come un
 * dato perso. Una prova nel Control Plane ora controlla che ogni nome di questa
 * tabella sia davvero canonico.
 */
const HIGHLIGHTED: Array<[string, () => string]> = [
  ['mgmt_ip', () => t('Indirizzo di gestione')],
  ['dns_name', () => t('Nome DNS')],
  ['hostname', () => t('Nome host')],
  ['sys_descr', () => t('Descrizione')],
  ['vendor', () => t('Produttore')],
  ['series', () => t('Serie')],
  ['model', () => t('Modello')],
  ['serial', () => t('Serial Number')],
  ['stack_member_count', () => t('Membri dello stack')],
  ['chassis_id', () => t('Chassis ID')],
  ['sys_location', () => t('Ubicazione')],
  ['sys_contact', () => t('Contatto')],
  ['os', () => t('Sistema operativo')],
  ['os_family', () => t('Famiglia del sistema')],
  ['software_rev', () => t('Versione software')],
  ['detected_at', () => t('Rilevato il')],
]

/**
 * La famiglia del sistema operativo, chiara: l'attributo `os_family` se c'è,
 * altrimenti dedotta dal testo dell'OS. Serve a mostrarla in evidenza e guida
 * la remediation (apt/dnf/zypper vs KB/WSUS).
 */
function famigliaOS(attributes: Record<string, unknown>): string {
  const esplicita = String(attributes.os_family ?? '').trim().toLowerCase()
  if (esplicita) return esplicita
  const testo = [attributes.os, attributes.guest_os, attributes.os_name]
    .map((x) => String(x ?? '')).join(' ').toLowerCase()
  if (/windows|win32|microsoft/.test(testo)) return 'windows'
  if (/darwin|mac ?os|macos/.test(testo)) return 'darwin'
  if (/bsd/.test(testo)) return 'bsd'
  if (/linux|ubuntu|debian|cent ?os|red ?hat|rhel|suse|fedora|alma|rocky|oracle/.test(testo)) return 'linux'
  return ''
}

/**
 * I nomi grezzi ancora presenti sui CI non più riscansionati.
 *
 * L'ingest li ritira alla prima scansione successiva, ma «alla prossima
 * scansione» per un apparato scandito una volta a settimana vuol dire fra sette
 * giorni: fino ad allora la scheda resterebbe mezza vuota per un CI che il dato
 * ce l'ha. È un ripiego con una scadenza, non una seconda tabella da mantenere.
 */
const LEGACY: Record<string, string> = {
  hostname: 'sysName',
  sys_descr: 'sysDescr',
  sys_location: 'sysLocation',
  sys_contact: 'sysContact',
}

function attributeOf(attributes: Record<string, unknown>, key: string): unknown {
  const value = attributes[key]
  if (value != null) return value
  const legacy = LEGACY[key]
  return legacy ? attributes[legacy] : undefined
}

/**
 * A displayed value as the tables search and sort it: the dash that stands
 * for «nothing» becomes empty, so it is not a value in the filter menus and
 * sorts last.
 */
function senzaTrattino(testo: string): string {
  return testo === '—' ? '' : testo
}

/**
 * Porta la riga al centro della vista e la fa lampeggiare un momento.
 *
 * Un'àncora semplice (`#comp-…`) saltava alla riga senza dire quale fosse: in
 * una tabella di cinquanta transceiver quasi uguali si atterrava «in mezzo
 * alla pagina» e la riga andava cercata a occhio. Qui lo scorrimento è
 * morbido, la riga finisce al centro e resta evidenziata per qualche secondo.
 */
function evidenziaRiga(id: string) {
  // The anchor sits in a cell of the shared table (which has no row ids):
  // the row is the one that contains it.
  const ancora = document.getElementById(id)
  const riga = ancora?.closest('tr') ?? ancora
  if (!riga) return
  riga.scrollIntoView({ behavior: 'smooth', block: 'center' })
  riga.classList.remove('riga-evidenziata')
  // Rilegge il layout: senza, togliere e rimettere la classe nello stesso
  // fotogramma non farebbe ripartire l'animazione al secondo clic.
  void riga.offsetWidth
  riga.classList.add('riga-evidenziata')
  window.setTimeout(() => riga.classList.remove('riga-evidenziata'), 3000)
}

// I nomi leggibili dei tipi di componente. Il valore grezzo (`transceiver`,
// `line_card`) è quello su cui si cerca e non cambia; qui si traduce solo per
// mostrarlo. Ogni voce chiama `t()` su una **stringa letterale**, non su una
// variabile: è così che l'estrattore delle traduzioni la vede, e la voce del
// dizionario non risulta orfana. Uno sconosciuto si mostra com'è.
const TIPI_COMPONENTE: Record<string, () => string> = {
  transceiver: () => t('Transceiver (SFP)'),
  line_card: () => t('Scheda'),
  power_supply: () => t('Alimentatore'),
  fan: () => t('Ventola'),
  module: () => t('Modulo'),
  chassis: () => t('Telaio'),
  stack_member: () => t('Membro dello stack'),
  // Le parti di un server viste dal suo controller di gestione (iDRAC).
  cpu: () => t('Processore'),
  memory: () => t('Banco di memoria'),
  disk: () => t('Disco'),
  nic: () => t('Scheda di rete'),
  storage_controller: () => t('Controller RAID'),
}

function tipoComponente(tipo: string): string {
  const etichetta = TIPI_COMPONENTE[tipo]
  return etichetta ? etichetta() : tipo
}

export default function CIDetail() {
  const { id = '' } = useParams()
  const { data, error, loading, reload } = useAsync(() => api.ci(id), [id])
  const relations = useAsync(() => api.ciRelations(id), [id])
  const changes = useAsync(() => api.ciChanges(id), [id])
  // La tabella MAC per porta (switch): quante voci per porta e la finestra
  // «cosa vede questa porta». Vuota sui CI che non commutano: la colonna non
  // compare. La lente arriva qui con `#porta-<nome>`: si apre da sola.
  const macPorte = useAsync(() => api.ciPortMacs(id), [id])
  const [portaAperta, setPortaAperta] = useState<string | null>(() => {
    const h = decodeURIComponent(window.location.hash || '')
    return h.startsWith('#porta-') ? h.slice('#porta-'.length) : null
  })
  // Le sezioni che mostrano i dati di un altro modulo seguono i permessi di
  // quel modulo (ruoli personalizzati): nascosto o disattivato → la sezione
  // non c'è; in lettura → c'è, senza i comandi. Il Control Plane rifiuta
  // comunque le chiamate (permessi_service.SOTTORISORSE_CI).
  const permessi = usePermessiModuli()
  const [installaAgente, setInstallaAgente] = useState(false)
  // The business services this CI is part of (saved hourly from each
  // service's composition): the section appears only when there are some.
  const serviziBusiness = useAsync(
    () => (permessi.vede('services') ? api.ciBusinessServices(id) : Promise.resolve(null)),
    [id],
  )
  const haServiziBusiness = (serviziBusiness.data?.services.length ?? 0) > 0 || (serviziBusiness.data?.apps?.length ?? 0) > 0
  // Contracts of the CI's asset and the extended support covering it
  // (support_extension_service): the section appears only when there are some.
  const contrattiCi = useAsync(
    () => (permessi.vede('contracts') ? api.ciContracts(id).catch(() => null) : Promise.resolve(null)),
    [id],
  )
  const haContratti = (contrattiCi.data?.contracts.length ?? 0) > 0 || (contrattiCi.data?.extensions.length ?? 0) > 0
  // Containers of this host (container_service): the section exists when there are some.
  const contenitori = useAsync(
    () => (permessi.vede('container_inventory') ? api.ciContainers(id).catch(() => null) : Promise.resolve(null)),
    [id],
  )
  const mostraContainer = (contenitori.data?.containers.length ?? 0) > 0
  // Runtime from OpenTelemetry: the section exists when the CI has traces.
  const runtime = useAsync(
    () => (permessi.vede('software_map') ? api.ciRuntime(id, 24 * 7).catch(() => null) : Promise.resolve(null)),
    [id],
  )
  const mostraRuntime = Boolean(runtime.data && (runtime.data.inbound.length || runtime.data.outbound.length))
  // CENSO SBOM: null without the licence or the module permission (the
  // section then does not exist).
  const sbom = useAsync(
    () => (permessi.vede('software_map') ? api.ciSbom(id).catch(() => null) : Promise.resolve(null)),
    [id],
  )
  // Eagle (monitoraggio): la sezione esiste solo se il tenant ha l'add-on.
  const eagle = useEagle()
  // CENSO MED: the clinical map around this CI, when it is a device of the
  // area or a clinical system (the API answers 404 otherwise).
  const med = useMed()
  // A medical device and a role that does not open CENSO MED: the CI stays,
  // its clinical engineering details (asset, maintenance contracts) do not.
  const medRiservato = Boolean(useStatoMed().riservato)
  const mappaClinica = useAsync(
    () => (med && permessi.vede('medical') ? api.medMap(`ci:${id}`).catch(() => null) : Promise.resolve(null)),
    [id, med],
  )
  const haMappaClinica = (mappaClinica.data?.edges.length ?? 0) > 0

  if (loading && !data) return <Spinner />
  if (error) return <ErrorBanner error={error} />
  if (!data) return null

  const uptime = data.attributes.uptime_seconds
  // The same predicate as med_scope_service.is_medical.
  const elettromedicale = data.ci_class === 'medical_device'
    || (typeof data.attributes.medical_category === 'string' && data.attributes.medical_category !== '')
  // Si nascondono le interfacce di solo loopback (IP 127/8) e broadcast/riservato
  // (255/8): rumore nell'elenco di rete, non schede utili.
  const interfacceVisibili = data.interfaces.filter((i) => !reteNascosta(i.ip))
  // FQDN (reverse DNS) e VRF/vpn-instance per IP: colonne presenti solo se
  // qualche interfaccia le ha, come per descrizione/stato/VLAN.
  const hasFqdn = interfacceVisibili.some((i) => i.fqdn)
  const hasVrfIf = interfacceVisibili.some((i) => i.vrf)
  // Su un apparato di rete le porte hanno descrizione e stato; su una VM no.
  const hasPortDetail = data.interfaces.some((i) => i.description || i.oper_status)
  // La colonna VLAN compare solo dove almeno una porta la dichiara: su un
  // server o un router senza bridge sarebbe sempre vuota.
  const hasVlan = data.interfaces.some((i) => i.vlans || i.pvid)
  // La SFP inserita in ogni porta, per nome interfaccia: lega la tabella delle
  // porte a quella dei componenti, così da una porta si salta al dettaglio del
  // suo transceiver invece di cercarlo a mano fra i componenti.
  // Per nome **canonico**: l'inventario dice «GigabitEthernet1/0/1», l'ifName
  // «Gi1/0/1» — alla lettera non si sarebbero mai incontrati.
  const sfpPerPorta = new Map(
    (data.components ?? [])
      .filter((c) => c.component_type === 'transceiver' && c.port)
      .map((c) => [nomePortaCanonico(c.port), c] as const),
  )
  const hasSfp = sfpPerPorta.size > 0
  const shown = new Set([
    ...HIGHLIGHTED.map(([k]) => k),
    // Anche i nomi grezzi: se l'anagrafica li ha usati come ripiego, ripeterli
    // sotto «Altri attributi» mostrerebbe due volte lo stesso fatto.
    ...HIGHLIGHTED.map(([k]) => LEGACY[k]).filter(Boolean),
  ])
  // Gli FQDN aggiuntivi hanno la loro scheda (con aggiungi/rimuovi): mostrarli
  // anche come attributo grezzo sarebbe lo stesso dato due volte.
  shown.add('additional_fqdns')
  // Il cluster ha la sua scheda (tipo, nodi, ruoli, VIP): gli stessi dati come
  // attributi grezzi — liste di oggetti — sarebbero illeggibili e ripetuti.
  if (data.ci_type === 'compute_cluster') {
    for (const k of ['cluster_type', 'cluster_node_count', 'cluster_vips', 'cluster_witness', 'cluster_quorum',
      'cluster_domain', 'cluster_functional_level', 'cluster_nodes', 'cluster_roles']) shown.add(k)
  }
  // Lo storage ha la sua scheda dedicata (dischi/pool/LUN): un array ha altri
  // attributi rispetto a un server o a uno switch, e le liste come JSON grezzo
  // in «Altri attributi» sarebbero illeggibili.
  const STORAGE_ATTRS = [
    'storage_mapping_count', 'storage_mappings',
    'storage_disk_count', 'storage_pool_count', 'storage_lun_count',
    'storage_disks', 'storage_pools', 'storage_luns',
    'storage_eth_port_count', 'storage_eth_ports',
    'storage_host_count', 'storage_hosts',
    'storage_initiator_count', 'storage_initiators',
  ]
  STORAGE_ATTRS.forEach((k) => shown.add(k))
  const haStorage = STORAGE_ATTRS.some((k) => data.attributes[k] != null)
  // Un bilanciatore ha la sua scheda (servizi virtuali, server reali,
  // certificati): le liste come JSON grezzo sarebbero illeggibili.
  const LB_ATTRS = [
    'lb_virtual_service_count', 'lb_virtual_services',
    'lb_real_server_count', 'lb_real_servers',
    'lb_certificate_count', 'lb_certificates',
  ]
  LB_ATTRS.forEach((k) => shown.add(k))
  const haBilanciamento = LB_ATTRS.some((k) => data.attributes[k] != null)
  const others = Object.entries(data.attributes).filter(([k]) => !shown.has(k))

  // Una base dati è un oggetto **software**, non un apparato: il motore in
  // ascolto e i database che contiene. IP pubblici, VIP, FQDN alternativi e
  // schede di rete appartengono all'host che la esegue, non a lei — mostrarli
  // sul suo dettaglio sono riquadri sempre vuoti che promettono un dato che da
  // qui non arriva. Ciò che conta di un DB (endpoint, motore, versione, porta,
  // i database dentro) sta negli attributi e nei collegamenti.
  const baseDati =
    ['database_instance', 'database'].includes(data.ci_class ?? '') ||
    ['database_instance', 'database'].includes(data.ci_type)

  // ntopng: la sezione «Network Security (ntopng)» compare se il tenant ha
  // ntopng configurato (sorgente live, come Wazuh) e il CI ha un indirizzo da
  // cercare — o se un vecchio job di arricchimento l'ha già marcato. Prima
  // serviva solo il marchio del job, che con ntopng live non arriva più: il
  // tab non compariva mai.
  // Un cluster è un raggruppamento, non una macchina: niente valutazione di
  // sicurezza, backup della configurazione, Wazuh, firewall, ntopng né schede di
  // rete — sezioni che lì sarebbero sempre vuote. Ha la sua scheda (SchedaCluster).
  const eCluster = data.ci_type === 'compute_cluster'
  // Un indirizzo aggiuntivo (VIP, IP d'interfaccia) non è una macchina: niente
  // valutazione di sicurezza, backup della configurazione, Wazuh né schede di
  // rete. IPAM, ntopng e firewall restano: il traffico verso un VIP conta.
  const eIndirizzo = data.ci_type === 'network_address'
  const certificati = (Array.isArray(data.attributes.tls_certificates) ? data.attributes.tls_certificates : []) as CertificatoTLS[]
  // Servers and VMs (or anything with a known system) can get the CENSO agent
  // installed remotely by a probe.
  const puoInstallareAgente = permessi.scrive('inventory_agents') && (
    ['host', 'virtual_machine'].includes(data.ci_type)
    || ['linux', 'windows'].includes(String(data.attributes.os_family ?? '').toLowerCase()))
  const haNtopng = !eCluster &&
    (Boolean(data.attributes.ntopng_monitored) ||
      (Boolean(data.ntopng_available) && Boolean(data.attributes.mgmt_ip))) &&
    permessi.vede('flows')
  // Wazuh è configurato per il tenant: la sezione compare e cerca l'agente
  // dell'host in tempo reale (non serve un job che l'abbia già marcato).
  const wazuhConfigurato = Boolean(data.wazuh_available)
  const haWazuh = !eCluster && !eIndirizzo && wazuhConfigurato && permessi.vede('wazuh')
  // Un firewall in tempo reale configurato (Palo Alto o FortiGate): si
  // interroga il traffico dell'host direttamente via API (completo, con «solo bloccato»).
  const haPaloAlto = Boolean(data.paloalto_available)
  // La sezione «Firewall» compare con almeno una sorgente: il traffico diretto
  // dal firewall, oppure il bloccato letto dai log del firewall in Wazuh.
  const haFirewall = !eCluster && (wazuhConfigurato || haPaloAlto) && permessi.vede('firewall')
  // Il CI *è* un firewall: allora ha senso il cruscotto (sistema, VPN, sessioni).
  const isFirewall =
    ['firewall'].includes(data.ci_class ?? '') || ['firewall'].includes(data.ci_type)
  // Un apparato di rete L3: la sezione «Routing» ha senso solo lì (le rotte le
  // raccoglie la discovery SNMP con l'opzione «routing_table»).
  const isNetworkDevice =
    ['network_device', 'router', 'switch', 'firewall', 'load_balancer'].includes(data.ci_type) ||
    ['router', 'switch', 'firewall', 'l3_switch', 'network_device'].includes(data.ci_class ?? '')
  // Un controller wireless: la sezione «Wireless» (AP, SSID, client) ha senso
  // solo lì — è lui che li dichiara, con l'inventario wireless via SSH.
  const isWirelessController = (data.ci_class ?? '') === 'wireless_controller'
  // Veeam è configurato per il tenant: la sezione «Protezione dati» compare sulle
  // VM e sui server e cerca in tempo reale l'oggetto di backup che li riguarda,
  // abbinato per nome. Solo su VM/server: un apparato di rete non si backup-a con
  // Veeam, e la sezione lì sarebbe sempre vuota.
  const haVeeam = Boolean(data.veeam_available)
  const isVmOrServer =
    ['virtual_machine', 'server', 'workstation'].includes(data.ci_class ?? '') ||
    ['virtual_machine', 'server', 'host'].includes(data.ci_type)
  const haProtezioneDati = haVeeam && isVmOrServer && permessi.vede('backup')
  // phpIPAM configurato: la sezione «IPAM» dice che cosa è documentato per gli
  // indirizzi di questo CI (o che non lo è affatto).
  const haIpam = Boolean(data.ipam_available) && permessi.vede('ipam')
  const mostraRouting = isNetworkDevice && permessi.vede('routing')
  // Anche un access point che dichiara da sé SSID e client (driver SNMP
  // Aerohive: attributi `wlan_vap_count`/`wlan_client_count`).
  const attrWlan = (data.attributes ?? {}) as Record<string, unknown>
  const apConWlan = (data.ci_class ?? '') === 'wireless_ap' && Boolean(attrWlan.wlan_vap_count || attrWlan.wlan_client_count)
  const mostraWireless = (isWirelessController || apConWlan) && permessi.vede('network')
  // On servers and VMs (where the agent reads it, or a vendor SBOM is
  // imported); elsewhere only when something is there.
  const mostraSbom = Boolean(sbom.data) && (isVmOrServer || (sbom.data?.applications.length ?? 0) > 0)
  const mostraSicurezza = !eCluster && !eIndirizzo && permessi.vede('security_health')
  // Patching (ADR-0024): add-on + permesso, e solo su macchine con un OS chiaro
  // (è lì che si applicano apt/dnf/zypper o le KB). Vedi SezionePatchingCI.
  const famigliaPatch = famigliaOS(data.attributes as Record<string, unknown>)
  const mostraPatching = !eCluster && !eIndirizzo && permessi.vede('patching') &&
    ['linux', 'windows'].includes(famigliaPatch)
  const mostraBackupConfig = !eCluster && !eIndirizzo && permessi.vede('device_configs')

  // Il controller di gestione (iDRAC, iLO, IMM…) in testa alla scheda: sul
  // server l'IP di Management, che è la prima cosa che si cerca quando la
  // macchina non risponde; sul controller, il server in cui sta. Dalle
  // relazioni `part_of`, con `bmc_ip` come ripiego finché non sono caricate.
  const legamiGestione = (relations.data ?? []).filter((r) => r.rel_type === 'part_of')
  const controllers = legamiGestione.filter(
    (r) => r.direction === 'incoming' && r.peer_type === 'management_controller',
  )
  const serverDelController =
    data.ci_type === 'management_controller'
      ? legamiGestione.filter((r) => r.direction === 'outgoing')
      : []
  const bmcIp = data.attributes.bmc_ip != null ? String(data.attributes.bmc_ip) : ''
  // La coppia in alta affidabilità (firewall, router in cluster): due CI
  // separati legati da `ha_peer_of`. In testa alla scheda, perché «chi è il
  // partner di questo apparato» è la prima domanda davanti a un failover.
  const peerHa = (relations.data ?? []).filter((r) => r.rel_type === 'ha_peer_of' && r.peer_id)

  // The port columns appear only when some row has them: on a VM with two
  // NICs they would be three empty columns, and an always-empty column makes
  // one conclude the discovery did not collect the data.
  const conMacPorte = (macPorte.data?.count ?? 0) > 0
  const sfpDi = (i: NetworkInterface) => (i.if_name ? sfpPerPorta.get(nomePortaCanonico(i.if_name)) : undefined)
  const colonneInterfacce: Column<NetworkInterface>[] = [
    { key: 'if_index', header: '#', type: 'number', value: (i) => i.if_index ?? '' },
    {
      key: 'if_name', header: t('Nome'), value: (i) => i.if_name ?? '',
      // The anchor the «Transceiver» column of the components jumps back to.
      render: (i) => <span id={i.if_name ? `porta-${i.if_name}` : undefined}>{i.if_name ?? '—'}</span>,
    },
    ...(hasPortDetail
      ? [
          { key: 'description', header: t('Descrizione'), value: (i: NetworkInterface) => i.description ?? '' },
          {
            key: 'stato', header: t('Stato'),
            value: (i: NetworkInterface) => (i.admin_status === 'down' ? t('disabilitata') : (i.oper_status ?? '')),
          },
        ]
      : []),
    ...(hasVlan
      ? [{ key: 'vlan', header: 'VLAN', mono: true, value: (i: NetworkInterface) => senzaTrattino(descriviVlan(i)) }]
      : []),
    { key: 'mac', header: 'MAC', mono: true, value: (i) => i.mac ?? '' },
    { key: 'ip', header: 'IP', mono: true, value: (i) => i.ip ?? '' },
    ...(hasFqdn ? [{ key: 'fqdn', header: 'FQDN', mono: true, value: (i: NetworkInterface) => i.fqdn ?? '' }] : []),
    ...(hasVrfIf ? [{ key: 'vrf', header: t('VRF/VPN-INSTANCE'), value: (i: NetworkInterface) => i.vrf ?? '' }] : []),
    // How many addresses the port has learnt (MAC table): opens the window
    // with what is attached. Only on the switches that read it.
    ...(conMacPorte
      ? [{
          key: 'dispositivi',
          header: <span title={t('Indirizzi MAC visti sulla porta')}>{t('Dispositivi collegati')}</span>,
          label: t('Dispositivi collegati'),
          type: 'number' as const,
          value: (i: NetworkInterface) => {
            const nome = i.if_name ?? ''
            const tutte = macPorte.data?.entries.filter((v) => v.if_name === nome).length ?? 0
            return tutte ? (macPorte.data?.by_port[nome] ?? 0) : ''
          },
          render: (i: NetworkInterface) => {
            const nome = i.if_name ?? ''
            const n = macPorte.data?.by_port[nome] ?? 0
            const sullaPorta = macPorte.data?.entries.filter((v) => v.if_name === nome) ?? []
            const tutte = sullaPorta.length
            // Il puntino giallo: almeno un MAC riconosciuto come
            // elettromedicale (catalogo CENSO o costruttore).
            const medici = sullaPorta.filter((v) => v.medical).length
            if (!tutte) return <span className="text-muted">—</span>
            return (
              <button type="button" className="link" onClick={() => setPortaAperta(nome)}
                      title={t('Apri la finestra della porta')}>
                {medici > 0 && (
                  <span className="pallino-giallo" role="img"
                        aria-label={t('Elettromedicali su questa porta')}
                        title={t('{n} elettromedicali su questa porta').replace('{n}', String(medici))} />
                )}{' '}
                {n}{tutte > n ? ` (+${tutte - n})` : ''}
              </button>
            )
          },
        }]
      : []),
    // La colonna compare solo se qualche porta ha una SFP: su uno switch
    // senza inventario avanzato sarebbe sempre vuota.
    ...(hasSfp
      ? [{
          key: 'transceiver', header: t('Transceiver'),
          value: (i: NetworkInterface) => {
            const sfp = sfpDi(i)
            return sfp ? sfp.model || sfp.name || 'SFP' : ''
          },
          render: (i: NetworkInterface) => {
            const sfp = sfpDi(i)
            return sfp ? (
              <a
                href={`#comp-${sfp.id}`}
                title={t('Vai al dettaglio del transceiver')}
                onClick={(e) => {
                  e.preventDefault()
                  evidenziaRiga(`comp-${sfp.id}`)
                }}
              >
                {sfp.model || sfp.name || 'SFP'}
              </a>
            ) : (
              '—'
            )
          },
        }]
      : []),
  ]

  // L'ancora della riga usa l'ifName dell'interfaccia, che può essere scritto
  // diversamente dalla porta del componente.
  const portaDi = (c: DeviceComponent) =>
    c.port ? data.interfaces.find((i) => stessaPorta(i.if_name, c.port)) : undefined
  const rimandiAllePorte = (data.components ?? []).some((c) => Boolean(portaDi(c)?.if_name))
  const colonneComponenti: Column<DeviceComponent>[] = [
    {
      key: 'tipo', header: t('Tipo'), value: (c) => tipoComponente(c.component_type),
      // The anchor the «Transceiver» column of the interfaces jumps to.
      render: (c) => <span id={`comp-${c.id}`}>{tipoComponente(c.component_type)}</span>,
    },
    // La posizione (Slot-1, Slot-2, POWER_1…) distingue due membri di uno
    // stack o due schede dello stesso modello: senza, sono righe identiche a
    // parte il seriale. Per un transceiver il «nome» è il tipo ottico e la
    // porta è il locatore: lì la posizione non aggiunge nulla.
    { key: 'posizione', header: t('Posizione'), value: (c) => (c.component_type === 'transceiver' ? '' : c.name || '') },
    {
      key: 'porta', header: t('Porta'), mono: true, value: (c) => c.port || '',
      // Il ritorno alla porta: chi arriva qui dalla colonna «Transceiver»
      // torna alla riga da cui era partito.
      render: (c) => {
        const porta = portaDi(c)
        return porta?.if_name ? (
          <a
            href={`#porta-${porta.if_name}`}
            title={t('Torna alla porta')}
            onClick={(e) => {
              e.preventDefault()
              evidenziaRiga(`porta-${porta.if_name}`)
            }}
          >
            {c.port}
          </a>
        ) : (
          c.port || '—'
        )
      },
    },
    { key: 'modello', header: t('Modello'), value: (c) => c.model || (c.component_type === 'transceiver' ? c.name : '') || '' },
    { key: 'seriale', header: t('Numero di serie'), mono: true, value: (c) => c.serial || '' },
    { key: 'produttore', header: t('Produttore'), value: (c) => c.vendor || '' },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{data.name ?? t('(senza nome)')}</h1>
          <p className="page__subtitle">
            <StatusBadge status={data.ci_type} />{' '}
            {/* L'identificativo permanente davanti alla chiave naturale: il
                primo non cambia mai, la seconda cambia appena una scansione
                legge qualcosa di più forte. Mostrarli insieme è il modo di far
                capire quale dei due si cita altrove. */}
            {data.ci_uid && <span className="mono">{data.ci_uid}</span>}{' '}
            <span className="mono">{data.primary_key}</span>
          </p>
          {/* I tag del CI, sotto il titolo: si leggono con il resto
              dell'identità e si cambiano da qui (catalogo e regole: /tags). */}
          <EditorTag tipo="ci" id={data.id} />
          {/* La striscia di sintesi: i fatti che si cercano per primi — che
              cos'è, dove risponde, che modello, come sta — a colpo d'occhio,
              senza dover aprire l'Anagrafica. Ogni voce compare solo se c'è. */}
          <div className="ci-sommario">
            {data.lifecycle_state && <LifecycleBadge state={data.lifecycle_state} />}
            {data.ci_class && (
              <span className="ci-sommario__item">
                <span className="ci-sommario__k">{t('Classe')}</span>
                {data.ci_class}
              </span>
            )}
            {data.attributes.mgmt_ip != null && (
              <span className="ci-sommario__item">
                <span className="ci-sommario__k">{t('Indirizzo')}</span>
                <span className="mono">{String(data.attributes.mgmt_ip)}</span>
              </span>
            )}
            {(bmcIp || controllers.length > 0) && (
              <span className="ci-sommario__item ci-sommario__item--evidenza">
                <span className="ci-sommario__k">{t('IP di Management')}</span>
                {bmcIp && (
                  <a className="mono" href={`https://${bmcIp}`} target="_blank" rel="noreferrer"
                    title={t('Apri la console del controller di gestione')}>
                    {bmcIp}
                  </a>
                )}
                {controllers.map((c) =>
                  c.peer_id ? (
                    <Link key={c.id} to={`/cis/${c.peer_id}`} title={t('Controller di gestione')}>
                      {c.peer_name ?? t('Controller di gestione')}
                    </Link>
                  ) : null,
                )}
              </span>
            )}
            {peerHa.length > 0 && (
              <span className="ci-sommario__item ci-sommario__item--evidenza"
                    title={t('Coppia in alta affidabilità: i due apparati condividono i MAC virtuali del cluster.')}>
                <span className="ci-sommario__k">{t('In HA con')}</span>
                {peerHa.map((r, i) => (
                  <Fragment key={r.id}>
                    {i > 0 && ', '}
                    <Link to={`/cis/${r.peer_id}`}>{r.peer_name ?? r.peer_id}</Link>
                  </Fragment>
                ))}
              </span>
            )}
            {data.probe && (
              <span className="ci-sommario__item ci-sommario__item--evidenza">
                <span className="ci-sommario__k">{t('Sonda CENSO')}</span>
                <Link to={`/fleet/${data.probe.id}`}>{data.probe.name}</Link>
              </span>
            )}
            {data.attributes.censo_appliance === true && (
              <span className="ci-sommario__item ci-sommario__item--evidenza"
                    title={t('Appliance CensoOS: la sonda di cattura dal filo')}>
                <span className="ci-sommario__k" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Marchio size={16} />{t('Censo Appliance')}
                </span>
                {data.attributes.censoos_version != null && <span className="mono">CensoOS {String(data.attributes.censoos_version)}</span>}
              </span>
            )}
            {data.attributes.temp_summary != null && (
              <span className="ci-sommario__item" title={t('Temperature dai sensori dell’appliance')}>
                <span className="ci-sommario__k">{t('Temperature')}</span>
                {String(data.attributes.temp_summary)}
              </span>
            )}
            {serverDelController.map((s) =>
              s.peer_id ? (
                <span key={s.id} className="ci-sommario__item ci-sommario__item--evidenza">
                  <span className="ci-sommario__k">{t('Controller di gestione di')}</span>
                  <Link to={`/cis/${s.peer_id}`}>{s.peer_name ?? s.peer_id}</Link>
                </span>
              ) : null,
            )}
            {(data.vendor || data.attributes.model != null) && (
              <span className="ci-sommario__item">
                <span className="ci-sommario__k">{t('Modello')}</span>
                {[data.vendor, data.attributes.model].filter(Boolean).map(String).join(' ')}
              </span>
            )}
            {/* The product line of a medical device when the exact model is not known yet. */}
            {data.attributes.series != null && data.attributes.model == null && (
              <span className="ci-sommario__item">
                <span className="ci-sommario__k">{t('Serie')}</span>
                {String(data.attributes.series)}
                <small className="text-muted"> · {t('modello da determinare')}</small>
              </span>
            )}
            {(() => {
              const osTesto = [data.attributes.os, data.attributes.guest_os, data.attributes.os_name]
                .map((x) => String(x ?? '').trim()).find(Boolean)
              const fam = famigliaOS(data.attributes as Record<string, unknown>)
              if (!osTesto && !fam) return null
              return (
                <span className="ci-sommario__item">
                  <span className="ci-sommario__k">{t('Sistema operativo')}</span>
                  {osTesto || fam}
                  {fam && osTesto && <span className="chip chip--muted" style={{ marginLeft: 6 }}>{fam}</span>}
                </span>
              )
            })()}
            <span className="ci-sommario__item">
              <span className="ci-sommario__k">{t('Ultimo rilevamento')}</span>
              {formatDateTime(data.last_seen)}
            </span>
          </div>
        </div>
        {puoInstallareAgente && (
          <button className="secondary" style={{ marginRight: 12 }} onClick={() => setInstallaAgente(true)}
            title={t('La sonda si collega alla macchina e installa l’agente CENSO')}>
            {t('Installa agente')}
          </button>
        )}
        {installaAgente && <InstallaAgente ciId={data.id} onChiudi={() => setInstallaAgente(false)} />}
        {/* Back to the list the user came from (Network, Security…), not
            always to the CI list. Without an in-app history, the CI list. */}
        <Link to="/cis" onClick={(e) => {
          if ((window.history.state as { idx?: number } | null)?.idx) {
            e.preventDefault()
            window.history.back()
          }
        }}>← {t('Torna all’elenco')}</Link>
      </header>

      {/* Non un errore: una correzione. L'oggetto era registrato due volte e
          ora è uno solo — chi arriva dal vecchio collegamento deve leggere
          perché la scheda che si apre ha un altro numero, invece di chiedersi
          se ha sbagliato indirizzo. */}
      {data.merged_from && (
        <div className="banner banner--info">
          {t('Questo oggetto era registrato più di una volta. L’identificativo {id} è stato unito a questo e continua a portare qui: i collegamenti salvati restano validi.', { id: data.merged_from })}
        </div>
      )}

      {/* I riquadri raggruppati per domanda — «che cos'è», «come sta», «dove
          risponde», «con chi parla» — invece di una colonna lunga da scorrere.
          Le sezioni sono poche e ordinate come si legge un oggetto: una voce per
          ogni riquadro sarebbe un secondo problema al posto del primo. */}
      <SectionedPage
        sections={[
          // L'ordine segue come si legge un oggetto: prima **che cos'è**
          // (anagrafica, classificazione), poi **come sta** (sicurezza, backup),
          // poi **dove risponde e con chi parla** (rete, collegamenti), infine il
          // dettaglio grezzo e la storia (attributi, ciclo di vita, tracciabilità).
          { id: 'anagrafica', label: t('Anagrafica') },
          { id: 'attributi', label: t('Attributi') },
          { id: 'classificazione', label: t('Classificazione') },
          { id: 'business-service', label: t('Business service') },
          { id: 'sbom', label: t('Software (SBOM)') },
          { id: 'runtime', label: t('Runtime') },
          { id: 'container', label: t('Container') },
          { id: 'eagle', label: 'Eagle' },
          { id: 'sicurezza', label: t('Sicurezza') },
          { id: 'patching', label: t('Patching') },
          { id: 'contratti', label: t('Contratti') },
          { id: 'certificati', label: t('Certificati') },
          { id: 'backup', label: t('Backup') },
          { id: 'protezione-dati', label: t('Protezione dati') },
          { id: 'rete', label: t('Rete') },
          { id: 'ipam', label: 'IPAM' },
          { id: 'network-security', label: t('Network Security (ntopng)') },
          { id: 'routing', label: t('Routing') },
          { id: 'wireless', label: t('Wireless') },
          { id: 'firewall', label: t('Firewall') },
          { id: 'wazuh', label: t('Wazuh') },
          { id: 'mappa-clinica', label: t('Mappa clinica') },
          { id: 'collegamenti', label: t('Collegamenti') },
          { id: 'ciclo-di-vita', label: t('Ciclo di vita') },
          { id: 'tracciabilita', label: t('Tracciabilità') },
          { id: 'attivita', label: t('Attività') },
        ].filter(
          (s) =>
            !(baseDati && s.id === 'rete') &&
            // Eagle: solo con l'add-on (e il permesso del modulo). Un cluster si
            // monitora nei suoi nodi, non come raggruppamento.
            !(s.id === 'eagle' && (!eagle || !permessi.vede('eagle') || eCluster)) &&
            // La sezione ntopng: vedi `haNtopng`.
            !(s.id === 'network-security' && !haNtopng) &&
            // Wazuh: compare se il tenant l'ha configurato.
            !(s.id === 'wazuh' && !haWazuh) &&
            // Firewall (traffico bloccato dai log): via Wazuh, quindi stesso gate.
            !(s.id === 'firewall' && !haFirewall) &&
            // Routing: solo sugli apparati di rete L3.
            !(s.id === 'routing' && !mostraRouting) &&
            // Wireless: solo sui controller, che sono chi dichiara gli AP.
            !(s.id === 'wireless' && !mostraWireless) &&
            !(s.id === 'sicurezza' && !mostraSicurezza) &&
            !(s.id === 'patching' && !mostraPatching) &&
            !(s.id === 'contratti' && !haContratti) &&
            !(s.id === 'backup' && !mostraBackupConfig) &&
            // Protezione dati (Veeam, Vinchin…): solo su VM/server e col tenant configurato.
            !(s.id === 'protezione-dati' && !haProtezioneDati) &&
            // IPAM: compare se il tenant ha phpIPAM configurato.
            !(s.id === 'ipam' && !haIpam) &&
            !(s.id === 'business-service' && !haServiziBusiness) &&
            !(s.id === 'certificati' && certificati.length === 0) &&
            !(s.id === 'sbom' && !mostraSbom) &&
            !(s.id === 'runtime' && !mostraRuntime) &&
            !(s.id === 'container' && !mostraContainer) &&
            !(s.id === 'mappa-clinica' && !haMappaClinica),
        )}
      >

      <PageSection id="classificazione">
        <Classification ci={data} onChanged={reload} />
        <Roles ci={data} />
      </PageSection>

      {haServiziBusiness && serviziBusiness.data && (
        <PageSection id="business-service">
          <SezioneServiziCI dati={serviziBusiness.data} />
        </PageSection>
      )}

      {mostraSbom && sbom.data && (
        <PageSection id="sbom">
          <SezioneSbomCI ciId={data.id} ciName={data.name ?? data.id} dati={sbom.data} onReload={() => void sbom.reload()}
            puoImportare={permessi.scrive('software_map')} />
        </PageSection>
      )}

      {mostraContainer && contenitori.data && (
        <PageSection id="container">
          <SezioneContainerCI dati={contenitori.data} />
        </PageSection>
      )}

      {mostraRuntime && (
        <PageSection id="runtime">
          <SezioneRuntimeCI ciId={data.id} />
        </PageSection>
      )}

      {mostraBackupConfig && (
        <PageSection id="backup">
          <ConfigurazioneSalvata
            ci={data}
            puoAvviare={permessi.scrive('device_configs') && permessi.scrive('jobs')}
          />
        </PageSection>
      )}

      {eagle && permessi.vede('eagle') && !eCluster && (
        <PageSection id="eagle">
          <SezioneEagle ciId={data.id} puoModificare={permessi.scrive('eagle')} />
        </PageSection>
      )}

      {haIpam && (
        <PageSection id="ipam">
          <SezioneIpam ciId={data.id} puoScrivere={permessi.scrive('ipam')} />
        </PageSection>
      )}

      {haProtezioneDati && (
        <PageSection id="protezione-dati">
          <SezioneProtezioneDati ciId={data.id} fornitori={data.backup_vendors ?? []} />
        </PageSection>
      )}

      {mostraSicurezza && (
        <PageSection id="sicurezza">
          <Sicurezza ciId={data.id} puoValutare={permessi.scrive('security_health')}
            sbomVulnerabili={mostraSbom ? (sbom.data?.vulnerable ?? 0) : 0} />
        </PageSection>
      )}

      {mostraPatching && (
        <PageSection id="patching">
          <SezionePatchingCI ciId={data.id} platform={famigliaPatch} scrive={permessi.scrive('patching')} />
        </PageSection>
      )}

      {haContratti && contrattiCi.data && (
        <PageSection id="contratti">
          <ContrattiDelCi dati={contrattiCi.data} />
        </PageSection>
      )}

      {certificati.length > 0 && (
        <PageSection id="certificati">
          <SezioneCertificati certificati={certificati} />
        </PageSection>
      )}

      {/* «Rete»: dove l'oggetto risponde. Gli IP pubblici e gli indirizzi
          aggiuntivi, uniti in una sezione sola — erano due schede per la stessa
          domanda. Non su una base dati: è software, e i suoi indirizzi sono
          quelli dell'host che la esegue. */}
      {!baseDati && (
        <PageSection id="rete">
          {data.ci_type === 'public_subnet' ? (
            <SubnetPubblica ci={data} onChanged={reload} />
          ) : (
            <IpPubblici ciId={data.id} />
          )}
          {/* Gli indirizzi aggiuntivi non si offrono su un indirizzo o una
              subnet: un VIP non ha a sua volta dei VIP. */}
          {data.ci_type !== 'public_ip' && data.ci_type !== 'public_subnet' &&
           data.ci_type !== 'network_address' && (
            <>
              <IndirizziAggiuntivi ciId={data.id} />
              <FqdnAggiuntivi ciId={data.id} />
            </>
          )}
        </PageSection>
      )}

      {haNtopng && (
        <PageSection id="network-security">
          <SezioneNetworkSecurity ciId={data.id} />
        </PageSection>
      )}

      {mostraRouting && (
        <PageSection id="routing">
          <SezioneRouting ciId={data.id} syncFirewall={haPaloAlto && isFirewall && permessi.scrive('routing')} />
        </PageSection>
      )}

      {mostraWireless && (
        <PageSection id="wireless">
          <SezioneWireless ciId={data.id} />
        </PageSection>
      )}

      {haFirewall && (
        <PageSection id="firewall">
          <SezioneFirewall ciId={data.id} haPaloAlto={haPaloAlto} haWazuh={wazuhConfigurato} />
        </PageSection>
      )}

      {haWazuh && (
        <PageSection id="wazuh">
          <SezioneWazuh ciId={data.id} />
        </PageSection>
      )}

      <PageSection id="ciclo-di-vita">
        <Lifecycle ci={data} onChanged={reload} />
        {elettromedicale && medRiservato ? (
          <div className="banner banner--info">
            {t('Elettromedicale: scheda, cespite, contratti e verifiche li gestisce l’Ingegneria clinica in CENSO MED.')}
          </div>
        ) : permessi.scrive('assets') && <PromoteToAsset ciId={id} />}
      </PageSection>

      <PageSection id="anagrafica">
      {/* Un indirizzo aggiuntivo si apre per sapere di chi è: prima il
          proprietario, la descrizione e cosa ne dice phpIPAM. */}
      {data.ci_type === 'network_address' && <AnagraficaIndirizzo ciId={data.id} haIpam={haIpam} />}
      {eCluster && <SchedaCluster ciId={data.id} />}
      <Card title={t('Anagrafica')}>
        <dl className="kv">
          {HIGHLIGHTED.filter(([key]) => attributeOf(data.attributes, key) != null).map(
            ([key, label]) => (
              // La chiave va sul Fragment: la forma breve `<>` non la accetta, e
              // le chiavi finivano sui figli — dove React non le guarda.
              <Fragment key={key}>
                <dt>{label()}</dt>
                <dd>{String(attributeOf(data.attributes, key))}</dd>
              </Fragment>
            ),
          )}
          {typeof uptime === 'number' && (
            <>
              <dt>{t('Uptime')}</dt>
              <dd>{formatUptime(uptime)}</dd>
            </>
          )}
          <dt>{t('Primo rilevamento')}</dt>
          <dd>{formatDateTime(data.first_seen)}</dd>
          <dt>{t('Ultimo rilevamento')}</dt>
          <dd>{formatDateTime(data.last_seen)}</dd>
          <dt>{t('Origine')}</dt>
          <dd className="mono">{data.source ?? '—'}</dd>
        </dl>
      </Card>

      {/* I campi che il tenant ha definito per questa classe di CI. Compare solo
          se ce n'è almeno uno applicabile. */}
      <SchedaCampiPersonalizzati
        entity="ci"
        scope={data.ci_class}
        values={data.custom_fields ?? {}}
        onSalva={async (valori) => {
          await api.setCiCustomFields(data.id, valori)
          reload()
        }}
      />

      {/* Un sistema di storage ha attributi propri: capacità in dischi, pool e
          LUN. La scheda compare solo se la discovery li ha raccolti. */}
      {haStorage && <StorageCard attributes={data.attributes} />}

      {/* Un bilanciatore: i servizi virtuali con i loro server reali e i
          certificati, letti dal driver SNMP del costruttore. */}
      {haBilanciamento && <LoadBalancerCard attributes={data.attributes} />}

      {/* Ciò che la macchina ha detto di sé nei protocolli sul filo (mirror /
          promiscuo), per i suoi indirizzi: compare solo se c'è qualcosa. */}
      <VistoSulFiloCard ciId={id} />

      {/* Un indirizzo, una subnet o un array di storage senza schede non hanno
          interfacce da mostrare: il riquadro vuoto con «si popolano con una
          discovery deep» prometterebbe un dato che non arriverà. */}
      {!baseDati && !eCluster && !eIndirizzo && data.ci_type !== 'public_ip' && data.ci_type !== 'public_subnet' &&
       !(haStorage && data.interfaces.length === 0) && (
      <>
      {portaAperta && macPorte.data && (
        <FinestraPortaMac
          ciId={id}
          porta={portaAperta}
          voci={macPorte.data.entries.filter((v) => v.if_name === portaAperta)}
          fingerbank={Boolean(macPorte.data.fingerbank)}
          onChiudi={() => setPortaAperta(null)}
          onAggiorna={() => void macPorte.reload()}
        />
      )}
      <Card title={t('Interfacce di rete ({n})', { n: interfacceVisibili.length })}>
        {interfacceVisibili.length === 0 ? (
          <EmptyState
            title={t('Nessuna interfaccia')}
            hint={t('Le interfacce si popolano con una discovery approfondita (deep).')}
          />
        ) : (
          // When the components link back to these ports, every row is
          // shown: a row on another page could not be reached.
          <DataTable
            columns={colonneInterfacce}
            rows={interfacceVisibili}
            rowKey={(i) => i.id}
            conteggio={{ nome: t('interfacce') }}
            paginazione={rimandiAllePorte ? false : undefined}
          />
        )}
      </Card>
      </>
      )}

      {/* I componenti fisici: solo dove l'inventario avanzato li ha raccolti.
          Un riquadro vuoto su ogni host prometterebbe un dato che non arriva —
          l'inventario SSH gira sui soli apparati di rete. */}
      {((data.components?.length ?? 0) > 0 || (haPaloAlto && isFirewall)) && (
        <Card title={t('Componenti ({n})', { n: data.components?.length ?? 0 })}
              actions={haPaloAlto && isFirewall && permessi.scrive('cis') ? (
                <OtticheDalFirewall ciId={data.id} onFatto={reload} />
              ) : undefined}>
          {(data.components?.length ?? 0) === 0 && (
            <p className="muted" style={{ marginTop: 0 }}>{t('Nessun componente ancora: le ottiche di un firewall Palo Alto o FortiGate si leggono via API con il pulsante qui sopra (la CLI del WatchGuard non le elenca).')}</p>
          )}
          {(data.components?.length ?? 0) > 0 && (
          // When the «Transceiver» column of the interfaces links here, every
          // row is shown: a row on another page could not be reached.
          <DataTable
            columns={colonneComponenti}
            rows={data.components!}
            rowKey={(c) => c.id}
            conteggio={{ nome: t('componenti') }}
            paginazione={hasSfp ? false : undefined}
          />
          )}
        </Card>
      )}
      </PageSection>

      <PageSection id="attributi">
        <DatiNonLetti attributes={data.attributes} />
        <OtherAttributes entries={others} sources={data.attribute_sources} relations={relations.data ?? undefined} />
      </PageSection>

      {haMappaClinica && mappaClinica.data && (
        <PageSection id="mappa-clinica">
          <Card title={t('Mappa clinica')}>
            <MappaClinica focus={`ci:${id}`} iniziale={mappaClinica.data} altezza={440} />
          </Card>
        </PageSection>
      )}

      <PageSection id="collegamenti">
        <Relations relations={relations.data} />
      </PageSection>

      {/* «Tracciabilità»: chi ha scritto cosa e quando (provenienza) e come è
          cambiato nel tempo (storico). Due risposte alla stessa domanda —
          «da dove viene questo dato» — riunite in una sezione sola. */}
      <PageSection id="tracciabilita">
        <Provenienza ciId={data.id} />
        <Changes changes={changes.data} />
      </PageSection>
      <PageSection id="attivita">
        <Attivita ciId={data.id} />
      </PageSection>
      </SectionedPage>

      {/* Sotto le sezioni, sempre visibile: è l'azione che non si annulla, e
          starle accanto alla classificazione la metterebbe a portata di clic
          distratto. Fuori dal contenitore a sezioni, così non sparisce cambiando
          scheda — ma resta l'ultima cosa, non la prima. */}
      <DeleteCI ci={data} />
    </>
  )
}

/**
 * La classe del CI, e il comando per correggerla.
 *
 * ## Perché serve un comando, e non solo una regola migliore
 *
 * Le regole di classificazione sbagliano e sbaglieranno sempre: un apparato che
 * nessuna parola chiave riconosce, un router che si descrive nominando il
 * sistema operativo su cui gira, un caso che il catalogo non prevede. Scrivere
 * una regola nuova a ogni caso è una rincorsa che non finisce, e nel frattempo
 * il CI resta classificato male in ogni elenco, export e ticket.
 *
 * La correzione fatta qui **resiste alle scansioni successive**: senza il segno
 * che la distingue, la classificazione automatica la sovrascriverebbe la notte
 * stessa — il modo più sicuro di far smettere di usare la funzione.
 *
 * ## Perché il motivo è sempre in vista
 *
 * «È un server» senza il perché non si può né controllare né correggere: chi
 * legge non sa se la classe viene da un dato dichiarato, da una parola trovata
 * nella descrizione o da una decisione di un collega. Sono tre gradi di fiducia
 * diversi e portano a tre azioni diverse.
 */
function Classification({
  ci,
  onChanged,
}: {
  ci: CIDetailType
  onChanged: () => void
}) {
  const taxonomy = useAsync(() => api.taxonomy(), [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [editing, setEditing] = useState(false)

  const categories = taxonomy.data?.categories ?? []
  const label =
    categories.flatMap((c) => c.classes).find((k) => k.key === ci.ci_class)?.label ??
    ci.ci_class ??
    t('non classificato')

  async function apply(next: string | null) {
    setBusy(true)
    setError(null)
    try {
      await api.setClassification(ci.id, next)
      setEditing(false)
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Classificazione')}>
      <ErrorBanner error={error} />
      <dl className="kv">
        <dt>{t('Classe')}</dt>
        <dd>
          {/* The reason (sysServices, a keyword, a colleague) stays as a
              tooltip: the field report asked for the class alone on screen. */}
          <span title={ci.class_reason ?? undefined}><StatusBadge status={label} /></span>
          {ci.class_manual ? (
            <span className="chip" title={t('Scelta da una persona: le regole non la toccano')}>
              {t('assegnata a mano')}
            </span>
          ) : (
            ci.class_confidence === 'low' && <span className="chip">{t('da verificare')}</span>
          )}
        </dd>
        {/* Il prodotto a catalogo, con accanto il **perché** dell'aggancio: è
            ciò che si guarda quando un asset esce con il codice di ordinazione
            o il costo sbagliato. Un aggancio senza motivo non si può né
            verificare né contestare — si può solo rifare a mano. */}
        {ci.sku && (
          <>
            <dt>{t('Prodotto a catalogo')}</dt>
            <dd>
              <code>{ci.sku.code}</code> — {ci.sku.label}
              {ci.sku.list_price != null && (
                <span className="muted">
                  {' '}
                  · {ci.sku.list_price.toLocaleString(localeCorrente())} {ci.sku.currency ?? ''}
                </span>
              )}
              {ci.sku_reason && <div className="muted">{ci.sku_reason}</div>}
            </dd>
          </>
        )}
      </dl>

      {!editing ? (
        <div className="rowactions" style={{ textAlign: 'left' }}>
          <button className="secondary" onClick={() => setEditing(true)}>
            {t('Cambia classe')}
          </button>
          {ci.class_manual && (
            <button className="secondary" disabled={busy} onClick={() => apply(null)}>
              {busy ? t('Attendere…') : t('Torna all’automatico')}
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="field__hint" style={{ marginTop: 12 }}>
            {t("La classe del dispositivo viene rilevata in automatico. Se non è corretta, scegli quella giusta nel menu Classe: la scelta resta anche nelle scansioni successive, finché non si torna all'automatico.")}
          </p>
          <div className="row">
            <div className="field" style={{ maxWidth: 320 }}>
              <label htmlFor="ci-class">{t('Classe')}</label>
              {/* Raggruppate per categoria: un elenco piatto di trenta voci
                  mette «switch» accanto a «bucket», e chi cerca la classe di un
                  apparato di rete le scorre tutte. */}
              <select
                id="ci-class"
                defaultValue={ci.ci_class ?? ''}
                disabled={busy}
                onChange={(e) => e.target.value && apply(e.target.value)}
              >
                <option value="">{t('Scegli…')}</option>
                {categories.map((c) => (
                  <optgroup key={c.key} label={c.label}>
                    {c.classes.map((k) => (
                      <option key={k.key} value={k.key}>
                        {k.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <button className="secondary" onClick={() => setEditing(false)} disabled={busy}>
              {t('Annulla')}
            </button>
          </div>
        </>
      )}
    </Card>
  )
}

/**
 * Cosa è cambiato su questo CI, e chi l'ha cambiato.
 *
 * ## Perché il job accanto all'integrazione
 *
 * Lo storico esisteva già nel database e non si vedeva da nessuna parte, il che
 * equivale a non averlo. È lo strumento per una domanda precisa: un valore che
 * oscilla fra due valori a ogni scansione. `source` non la risponde — due
 * discovery SNMP dicono entrambe «snmp» — mentre il job, con il bersaglio che
 * scandiva, dice **quali due scansioni** se lo contendono. È la differenza fra
 * ipotizzare che due apparati stiano finendo sullo stesso CI e vederlo.
 *
 * ## Perché le righe sono chiuse per difetto
 *
 * Un elenco lungo di attributi cambiati seppellirebbe la parte utile. La
 * tabella mostra le ultime modifiche; il resto sta dietro un dettaglio, che è
 * dove si guarda quando la prima schermata non basta.
 */
function Changes({ changes }: { changes: CIChange[] | null }) {
  const rows = changes ?? []
  const posizione = new Map(rows.map((c, n) => [c, n]))

  const colonne: Column<CIChange>[] = [
    {
      key: 'at', header: t('Quando'), type: 'date', value: (c) => c.at,
      render: (c) => <span title={c.at}>{formatDateTime(c.at)}</span>,
    },
    {
      key: 'field', header: t('Campo'),
      value: (c) => (c.kind !== 'attribute' ? `${c.label} ${c.kind}` : c.label),
      render: (c) => (
        <>
          {c.label}
          {c.kind !== 'attribute' && <span className="chip">{c.kind}</span>}
        </>
      ),
    },
    // A change of a list is told element by element, in the «Da» cell (it
    // used to span both «Da» and «A»).
    {
      key: 'old', header: t('Da'), value: (c) => senzaTrattino(scalar(c.old)),
      render: (c) => {
        const diff = diffLista(c.old, c.new)
        return diff ? <DiffElenco diff={diff as DiffLista} /> : <span className="mono">{scalar(c.old)}</span>
      },
    },
    {
      key: 'new', header: t('A'), value: (c) => senzaTrattino(scalar(c.new)),
      render: (c) => (diffLista(c.old, c.new) ? null : <span className="mono">{scalar(c.new)}</span>),
    },
    { key: 'source', header: t('Origine'), mono: true, value: (c) => c.source },
    {
      key: 'job', header: t('Scansione'), value: (c) => c.job ?? '',
      // L'identificativo del job sta nel `title`: in colonna occuperebbe metà
      // tabella con trentasei caratteri che nessuno legge, ma serve quando si
      // va a cercare il rapporto di quella scansione.
      render: (c) => (
        <span title={c.job_id ?? t('nessun job: conclusione del Control Plane')}>{c.job ?? '—'}</span>
      ),
    },
  ]

  return (
    <Card title={t('Storico delle modifiche ({n})', { n: rows.length })}>
      {rows.length === 0 ? (
        <EmptyState
          title={t('Nessuna modifica registrata')}
          hint={t('Si registrano solo i cambiamenti di valore: un CI appena scoperto non ne ha, e una discovery che riscrive gli stessi dati non ne produce.')}
        />
      ) : (
        <DataTable
          columns={colonne}
          rows={rows}
          // The position in the API's list: a key that does not move when
          // the table is sorted (an opened list detail stays on its row).
          rowKey={(c) => `${c.at}-${c.field}-${posizione.get(c)}`}
          conteggio={{ nome: t('modifiche') }}
        />
      )}
    </Card>
  )
}

/**
 * Un cambiamento di elenco detto per elemento: una riga di riassunto («16
 * elementi: aggiunto wwn · 1 aggiunto») e il dettaglio sotto, chiuso.
 */
function DiffElenco({ diff }: { diff: DiffLista }) {
  const campi = campiRiassunti(diff)
  const parti: string[] = []
  if (diff.aggiunti.length) parti.push(t('{n} aggiunti', { n: diff.aggiunti.length }))
  if (diff.tolti.length) parti.push(t('{n} tolti', { n: diff.tolti.length }))
  if (diff.cambiati.length) {
    const come = campi
      .slice(0, 4)
      .map((c) =>
        c.come === 'aggiunto'
          ? t('aggiunto {campo}', { campo: c.campo })
          : c.come === 'tolto'
            ? t('tolto {campo}', { campo: c.campo })
            : t('cambiato {campo}', { campo: c.campo }),
      )
      .join(', ')
    parti.push(t('{n} elementi: {come}', { n: diff.cambiati.length, come }))
  }
  if (!parti.length) return <span className="text-muted">{t('stesso contenuto, in altro ordine')}</span>
  const valore = (v: unknown) => (v === undefined || v === null ? '—' : scalar(v))
  return (
    <details>
      <summary>{parti.join(' · ')}</summary>
      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
        {diff.aggiunti.map((e, i) => (
          <li key={`a${i}`} className="mono">+ {nomeElemento(e, diff.chiave)}</li>
        ))}
        {diff.tolti.map((e, i) => (
          <li key={`t${i}`} className="mono">− {nomeElemento(e, diff.chiave)}</li>
        ))}
        {diff.cambiati.map((el) => (
          <li key={`c${el.chiave}`} className="mono">
            ~ {el.chiave}:{' '}
            {el.campi.map((c) => `${c.campo} ${valore(c.da)} → ${valore(c.a)}`).join('; ')}
          </li>
        ))}
      </ul>
    </details>
  )
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return t('{d} g {h} h', { d, h })
  if (h > 0) return `${h} h ${m} min`
  return `${m} min`
}


/**
 * Che cosa fa questo CI, e come si è concluso.
 *
 * ## Perché non basta la classe
 *
 * La classe è una sola, e deve restarlo: è la dimensione su cui si filtra, si
 * raggruppa e si mappa verso un ITSM. Ma un oggetto solo fa più cose — un
 * server che ospita venti macchine virtuali e che ha un iDRAC che lo amministra
 * fuori banda è tre fatti. Finché l'unico posto per dirli era la classe, si
 * contendevano lo stesso campo: la fusione di due CI non tocca la classe, e
 * vinceva l'ordine di scansione.
 *
 * ## Perché il motivo sta accanto, sempre
 *
 * Perché il ruolo è **dedotto**, e una deduzione senza la sua prova non si può
 * contestare. «Ospita 12 macchine virtuali» si verifica; «hypervisor» si può
 * solo credere. È anche il posto in cui l'arricchimento diventa visibile: il
 * motivo del ruolo «gestito fuori banda» porta il nome e l'indirizzo del
 * controller, che stanno su un altro CI.
 */
/**
 * Lo stato del backup della configurazione, sulla scheda dell'apparato.
 *
 * ## Perché serve qui e non basta la pagina delle configurazioni
 *
 * Perché sono due domande diverse. Quella pagina risponde a «cosa è cambiato
 * sul parco»; questa scheda risponde a «di **questo** apparato ho una copia?»,
 * ed è la domanda che ci si fa guardando l'apparato — dopo un guasto, prima di
 * una modifica, quando qualcuno chiede se si può tornare indietro.
 *
 * Senza, l'unico modo di saperlo era andare in un'altra pagina e cercarlo in
 * elenco, il che significa che nel momento in cui serve non lo si sa.
 *
 * ## Perché «mai salvata» non basta come risposta
 *
 * «Non c'è una copia» e «non c'è un job che la prenda» sono due cose diverse
 * con due rimedi diversi, e la prima da sola manda a cercare un guasto che non
 * c'è. Quando non c'è nessuna revisione lo si dice **con il motivo probabile**.
 */
/**
 * La postura di questo asset, e il pulsante per ricalcolarla.
 *
 * ## Perché il pulsante, se la valutazione gira già con le scansioni
 *
 * Perché le due domande arrivano in momenti diversi. La valutazione che gira
 * con le raccolte risponde a «com'è messo il parco»; questo pulsante risponde a
 * «e adesso?», dopo aver chiuso una porta o aggiornato un pacchetto. Aspettare
 * la scansione notturna per sapere se il rimedio ha funzionato vuol dire non
 * saperlo, perché domani ci si è dimenticati della domanda.
 *
 * ## Perché non tocca la rete
 *
 * Rilegge i fatti già in inventario. Quindi risponde subito e funziona anche su
 * un host spento — e per la stessa ragione i fatti valgono quanto l'ultima
 * raccolta, cosa che l'esito dice.
 */
/** Le azioni che chiudono i reperti di questo asset, dalla più urgente. */
function CosaFare({ righe }: { righe: SecurityFinding[] }) {
  const azioni = azioniDa(righe)
  if (azioni.length === 0) return null
  return (
    <ol className="azioni">
      {azioni.map((a) => (
        <li key={a.cosa + (a.comando ?? '')}>
          <span className={classeGravita(a.gravita)}>{t(a.gravita)}</span>{' '}
          {t(a.cosa, a.valori)}{' '}
          <span className="muted">
            {t('— chiude {chiude} rischi', { chiude: a.chiude })}
          </span>
          {a.comando && <pre className="comando">{a.comando}</pre>}
        </li>
      ))}
    </ol>
  )
}

//: Quanti reperti si mostrano sulla scheda di un asset.
//:
//: La scheda risponde a «com'è messo questo asset», e per quello bastano i più
//: gravi: cento righe sotto l'anagrafica rendono illeggibile tutta la pagina,
//: non solo quella sezione. L'elenco completo ha una pagina sua.
const MAX_IN_SCHEDA = 12

/** Il colore della gravità. Le fasce sono quelle di CVSS, che le dichiara. */
function classeGravita(gravita: string): string {
  switch (gravita) {
    case 'critica':
      return 'badge badge--error'
    case 'alta':
      return 'badge badge--warn'
    case 'media':
      return 'badge badge--info'
    default:
      return 'badge badge--muted'
  }
}

function Sicurezza({ ciId, puoValutare, sbomVulnerabili }: {
  ciId: string
  puoValutare: boolean
  /** Vulnerable libraries of the machine's SBOM: listed in the SBOM section, not here. */
  sbomVulnerabili: number
}) {
  // The library vulnerabilities (SBOM) stay out of this section: they belong to
  // a component of an application, and the SBOM section shows them next to it.
  const reperti = useAsync(() => api.ciFindings(ciId, { excludeSbom: true }), [ciId])
  const [inCorso, setInCorso] = useState(false)
  const [esito, setEsito] = useState<AssessmentResult | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  // Quale delle due si sta eseguendo, per dirlo mentre gira e dopo.
  const [conRete, setConRete] = useState(false)

  const righe = reperti.data ?? []

  // Due pulsanti e non una casella accanto a uno.
  //
  // La casella c'era, e non funzionava: la scelta e l'azione erano due gesti,
  // il primo si dimenticava, e il risultato non diceva quale dei due modi
  // avesse girato — «0 reperti» usciva identico. Chi aveva chiesto i controlli
  // sui servizi non aveva modo di accorgersi di non averli ottenuti, che è
  // esattamente il silenzio che questa funzione esiste per non produrre.
  //
  // Un pulsante dice cosa fa e non si può lasciare a metà.
  const valuta = async (rete: boolean) => {
    setInCorso(true)
    setConRete(rete)
    setErrore(null)
    try {
      const r = await api.runAssessment([ciId], rete)
      setEsito(r)
      // Con `local` i reperti sono già scritti: si ricarica e si vede.
      // Con `job` arriveranno quando una sonda prenderà il lavoro, e
      // ricaricare adesso mostrerebbe ancora i vecchi — per questo
      // l'esito dice dove è girata invece di far indovinare.
      // **Atteso**: il pulsante resta «in corso» finché la lista non è quella
      // nuova. Senza l'attesa, l'esito compare accanto ai reperti di prima —
      // si è visto «10 reperti aperti» sopra un elenco che ne conteneva uno, e
      // per giunta quello appena chiuso.
      if (r.mode === 'local') await reperti.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Card
      title={righe.length > 0 ? t('Sicurezza ({n})', { n: righe.length }) : t('Sicurezza')}
      actions={
        // In sola lettura la sezione si vede, ma i due comandi no.
        puoValutare ? (
        <>
          <button
            className="secondary"
            disabled={inCorso}
            onClick={() => valuta(false)}
          >
            {inCorso && !conRete ? t('Valutazione in corso…') : t('Esegui valutazione')}
          </button>
          <button
            className="secondary"
            disabled={inCorso}
            style={{ marginLeft: '0.5rem' }}
            title={t('Apre una connessione TLS verso le porte cifrate di questo asset: certificati scaduti, TLS obsoleto, cifrari deboli. Nessuna credenziale provata, nessun dato inviato.')}
            onClick={() => valuta(true)}
          >
            {inCorso && conRete
              ? t('Controllo dei servizi in corso…')
              : t('Valuta e controlla i servizi')}
          </button>
        </>
        ) : undefined
      }
    >
      <ErrorBanner error={errore ?? reperti.error} />

      {/* Il banner dice **quale** dei due modi ha girato. Senza, l'esito è
          identico nei due casi, e chi ha chiesto i controlli sui servizi non ha
          modo di sapere se li ha ottenuti. */}
      {esito?.mode === 'job' && (
        <div className="banner banner--ok">
          {conRete
            ? t('Valutazione con i controlli sui servizi affidata a una sonda — è l’unica che arriva su quella rete. I rischi compariranno qui appena la prende in carico.')
            : t('La valutazione è stata affidata a una sonda: i rischi compariranno qui appena la prende in carico.')}
        </div>
      )}
      {esito?.mode === 'local' && (
        <div className="banner banner--ok">
          {t('Valutato su {assessed} asset: {findings} rischi aperti, {closed} non più validi.', {
            assessed: esito.assessed ?? 0,
            findings: esito.findings ?? 0,
            closed: esito.closed ?? 0,
          })}
        </div>
      )}
      {/* Le lacune subito sotto l'esito, non in fondo: dicono **quanto vale**
          il numero appena letto, e messe altrove si leggono dopo aver già
          creduto al numero. */}
      {(esito?.gaps ?? []).map((lacuna) => (
        <div className="banner banner--warn" key={lacuna}>
          {t(lacuna)}
        </div>
      ))}

      {reperti.loading && !reperti.data ? (
        <Spinner />
      ) : righe.length === 0 ? (
        <p className="muted">
          {t('Nessun elemento di sicurezza rilevato. L’ultima valutazione è ancora valida: per aggiornarla con i dati attuali dell’inventario, premi «Esegui valutazione».')}
        </p>
      ) : (
        <>
          {/* Il riepilogo per gravità **prima** dell'elenco.
              Cento righe non si leggono: si guarda quante sono le gravi, e si
              scende solo se ce ne sono. Senza questa riga bisogna scorrere
              tutto per sapere se c'è qualcosa che scotta. */}
          <p className="stato-backup">
            {(['critica', 'alta', 'media', 'bassa'] as const).map((g) => {
              const quante = righe.filter((r) => r.severity === g).length
              if (quante === 0) return null
              return (
                <span className={classeGravita(g)} key={g}>
                  {quante} {t(g)}
                </span>
              )
            })}
          </p>

          {/* Cosa fare, prima di cosa c'è: su ottanta reperti la domanda non è
              «quali sono» ma «cosa devo fare», e la risposta è quasi sempre un
              comando solo. */}
          <CosaFare righe={righe} />

          <p className="muted" style={{ marginBottom: 4 }}>
            {t('I rischi, per chi vuole controllare')}
          </p>
          <dl className="kv">
            {righe.slice(0, MAX_IN_SCHEDA).map((r) => (
              <Fragment key={r.id}>
                <dt>
                  <span className={classeGravita(r.severity)}>{t(r.severity)}</span>
                </dt>
                <dd>
                  <TestoConKev testo={t(r.title, r.evidence)} cve={cveDaProva(r.evidence)} />
                  {r.remedy && (
                    <>
                      {' — '}
                      <span className="muted">{t(r.remedy)}</span>
                    </>
                  )}
                </dd>
              </Fragment>
            ))}
          </dl>

          {/* Il taglio **si dichiara**: un elenco troncato in silenzio si legge
              come completo, e su una funzione di sicurezza «non c'è altro» è
              la conclusione sbagliata. */}
          {righe.length > MAX_IN_SCHEDA && (
            <p className="muted">
              {t('Mostrati i {mostrati} più gravi su {totale}.', {
                mostrati: MAX_IN_SCHEDA,
                totale: righe.length,
              })}{' '}
              <Link to="/security-health">{t('Vedili tutti')}</Link>
            </p>
          )}
        </>
      )}

      {sbomVulnerabili > 0 && (
        <p className="muted">
          {t('Le vulnerabilità delle librerie delle applicazioni ({n} componenti) non sono qui: sono nella sezione', { n: sbomVulnerabili })}{' '}
          <a href="#sbom">{t('Software (SBOM)')}</a>.
        </p>
      )}
      <p className="muted">
        <Link to="/security-health">{t('Tutti i rischi del parco')}</Link>
      </p>
    </Card>
  )
}

function ConfigurazioneSalvata({ ci, puoAvviare }: { ci: CIDetailType; puoAvviare: boolean }) {
  // Solo per gli apparati che una configurazione da salvare ce l'hanno. Su un
  // server la scheda direbbe «nessuna copia» per sempre, cioè segnalerebbe un
  // problema che non esiste — ed è il modo in cui un avviso smette di essere
  // letto.
  // Per TIPO o per CLASSE: un router scoperto da uno sweep entra come
  // `unmanaged_host` ma la classificazione lo riconosce — e il suo backup
  // serve quanto quello di uno switch. Guardare solo il tipo lasciava senza
  // sezione proprio gli apparati riconosciuti meglio.
  const salvabile =
    ['network_device', 'firewall', 'router', 'switch', 'load_balancer'].includes(ci.ci_type) ||
    [
      'switch',
      'router',
      'firewall',
      'load_balancer',
      'wireless_controller',
      'wireless_ap',
      'optical_transport',
      'network_appliance',
      'console_server',
    ].includes(ci.ci_class ?? '')

  const stato = useAsync(
    () => (salvabile ? api.configStatus(ci.id) : Promise.resolve(null)),
    [ci.id, salvabile],
  )

  if (!salvabile) return null
  if (stato.loading) {
    return (
      <Card title={t('Backup della configurazione')}>
        <Spinner />
      </Card>
    )
  }

  const s = stato.data
  if (!s) return null

  return (
    <Card title={t('Backup della configurazione')}>
      <p className="stato-backup">
        <BackupBadge giudizio={s.giudizio} />{' '}
        {s.attivo ? (
          <>
            <strong>{t('{n} versioni', { n: s.versioni })}</strong>
            {' · '}
            {t('controllato {quando}', {
              quando: formatDateTime(s.last_checked_at),
            })}
          </>
        ) : null}
      </p>

      {s.attivo ? (
        <dl className="kv">
          <dt>{t('Ultima modifica')}</dt>
          <dd>
            {/* Le due date **insieme** sono la lettura giusta: «controllato ieri,
                fermo da sei mesi» dice che il backup funziona e che nessuno ha
                toccato l'apparato. Da sola, la seconda si legge come un guasto. */}
            {s.last_change_at ? (
              formatDateTime(s.last_change_at)
            ) : (
              <span className="muted">{t('mai cambiata da quando è sotto backup')}</span>
            )}
          </dd>
          <dt>{t('Letta come')}</dt>
          <dd>
            {s.family ? <span className="badge">{s.family}</span> : null}{' '}
            {s.transport ? (
              <span className="badge badge--info">{s.transport}</span>
            ) : null}
          </dd>
        </dl>
      ) : (
        <p className="muted">
          {t(
            'Nessuna copia della configurazione. Di solito perché non c’è ancora un job di tipo «Backup configurazioni» che comprenda questo apparato — non perché il salvataggio sia fallito: in quel caso il rapporto del job lo direbbe.',
          )}
        </p>
      )}

      {/* La storia di QUESTO apparato qui, senza rimandare alla pagina delle
          configurazioni: statistiche, grafici, versioni con il confronto e il
          testo intero (componenti condivisi con quella pagina). */}
      {s.versioni > 0 ? <StoriaConfigurazione ciId={ci.id} nome={ci.name ?? ci.primary_key} /> : null}

      {puoAvviare && <ImpostaBackup ci={ci} />}
    </Card>
  )
}

/**
 * Il backup di QUESTO apparato, avviato dalla sua scheda.
 *
 * La strada maestra resta il job «Backup configurazioni» su tutta la rete; ma
 * l'apparato appena arrivato, o quello che il giro grande salta, si sistema da
 * qui: credenziale SSH, avvia, e il job è lo stesso identico job — un solo
 * bersaglio. Nessun secondo meccanismo da tenere allineato al primo.
 */
function ImpostaBackup({ ci }: { ci: CIDetailType }) {
  const [aperto, setAperto] = useState(false)
  const [credenziale, setCredenziale] = useState('')
  const [busy, setBusy] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const credenziali = useAsync(
    () => (aperto ? api.credentials() : Promise.resolve([])),
    [aperto],
  )
  const indirizzo = String(ci.attributes?.mgmt_ip ?? '')

  async function avvia() {
    setBusy(true)
    setError(null)
    try {
      const job = await api.createJob({
        kind: 'config_backup',
        targets: [indirizzo],
        credential_id: credenziale,
      } as JobCreate)
      setEsito(
        t('Backup avviato (job {id}…): l’esito compare qui sopra e nel rapporto del job.', {
          id: job.id.slice(0, 8),
        }),
      )
      setAperto(false)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  if (!indirizzo) {
    return (
      <p className="field__hint">
        {t('Per avviare un backup da qui serve l’indirizzo di gestione, che questo CI non ha.')}
      </p>
    )
  }

  return (
    <>
      <ErrorBanner error={error} />
      {esito && <p className="field__hint">{esito}</p>}
      {!aperto ? (
        <div className="rowactions" style={{ textAlign: 'left' }}>
          <button className="secondary" onClick={() => setAperto(true)}>
            {t('Imposta backup')}
          </button>
        </div>
      ) : (
        // In colonna, non in riga: con un nome di credenziale lungo la tendina
        // si allargava e spingeva «Salva ora» fuori dalla scheda. Il menu ha un
        // tetto di larghezza e i pulsanti stanno **sotto**, così restano sempre
        // visibili qualunque sia il nome.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <select
            value={credenziale}
            onChange={(e) => setCredenziale(e.target.value)}
            aria-label={t('Credenziale SSH per il backup')}
            style={{ maxWidth: 320, width: '100%' }}
          >
            <option value="">{t('credenziale SSH…')}</option>
            {(credenziali.data ?? [])
              .filter((c) => c.kind === 'ssh_key' || c.kind === 'ssh_password')
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <button onClick={avvia} disabled={busy || !credenziale}>
              {busy ? t('Avvio…') : t('Salva ora')}
            </button>
            <button className="secondary" onClick={() => setAperto(false)} disabled={busy}>
              {t('Annulla')}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Il pallino: verde, giallo, rosso o grigio.
 *
 * Il giudizio arriva dal server già preso; qui si sceglie solo il colore e la
 * parola. Ricalcolare le soglie da questo lato vorrebbe dire tenerne due copie,
 * ed è la coppia che si separa in silenzio.
 */
function BackupBadge({ giudizio }: { giudizio: ConfigBackupStatus['giudizio'] }) {
  const aspetto: Record<
    ConfigBackupStatus['giudizio'],
    { classe: string; testo: string; spiega: string }
  > = {
    recente: {
      classe: 'badge badge--ok',
      testo: t('Backup attivo'),
      spiega: t('Letto con successo di recente.'),
    },
    vecchio: {
      classe: 'badge badge--warn',
      testo: t('Backup in ritardo'),
      spiega: t(
        'Nessuna lettura riuscita da più di una settimana: il job potrebbe non essere più in pianificazione, o non riuscire ad accedere all’apparato.',
      ),
    },
    fermo: {
      classe: 'badge badge--error',
      testo: t('Backup fermo'),
      spiega: t(
        'Nessuna lettura riuscita da oltre un mese: la copia che c’è non descrive più l’apparato con abbastanza fiducia da ripristinarla senza guardarla.',
      ),
    },
    mai: {
      classe: 'badge',
      testo: t('Mai salvata'),
      spiega: t('Questo apparato non è mai stato letto con successo.'),
    },
  }
  const a = aspetto[giudizio]
  return (
    <span className={a.classe} title={a.spiega}>
      {a.testo}
    </span>
  )
}

function Roles({ ci }: { ci: CIDetailType }) {
  const roles = Object.entries(ci.roles ?? {})
  if (roles.length === 0) return null

  return (
    <Card title={t('Che cosa fa')}>
      <dl className="kv">
        {roles.map(([role, reason]) => (
          <Fragment key={role}>
            <dt>
              <RoleBadge role={role} reason={reason} />
            </dt>
            <dd>{reason}</dd>
          </Fragment>
        ))}
      </dl>
      <p className="field__hint">
        {t('Dedotto dalle relazioni, non dichiarato: sparisce da solo quando sparisce il fatto.')}
      </p>
    </Card>
  )
}

/**
 * Lo stato nel tempo, e il comando per dichiarare dismesso un apparato.
 *
 * ## Perché questo riquadro esiste
 *
 * Lo stato veniva calcolato, aveva un motivo scritto per essere letto da una
 * persona, e non era mostrato da nessuna parte. Un dato che nessuno può vedere
 * è un dato che non c'è.
 *
 * ## Perché il comando sta qui e non fra le azioni pericolose
 *
 * Perché non è pericoloso, ed è quasi sempre la cosa giusta. Tutti gli altri
 * stati sono conclusioni che il Control Plane ricava dalle scansioni; questo
 * no — nessuna scansione può distinguere una macchina rottamata da una spenta
 * perché è saltato il gruppo di continuità. Finché non si poteva dichiarare,
 * chi voleva dire «questa l'abbiamo dismessa» aveva un solo gesto: cancellare
 * il CI, in fondo alla pagina, che butta la storia e — se l'apparato è ancora
 * acceso — si annulla da solo alla scansione dopo.
 */
/**
 * Gli indirizzi pubblici che espongono questo CI.
 *
 * Ogni indirizzo è un CI a sé (`public_ip`), legato da una relazione
 * `exposed_via`: lo stesso indirizzo — il NAT, il VIP — espone più CI insieme,
 * e la sua reputazione vive su di lui, verificata una volta per tutti. Da qui
 * si dichiara la lista e si va alla scheda dell'indirizzo; gli esiti sono tre,
 * e il terzo è il più importante: «non verificabile» quando la query non ha
 * avuto risposta — un «pulito» inventato sarebbe un dato plausibile e
 * sbagliato.
 */
/**
 * La scheda di una subnet pubblica: la tabella degli indirizzi, dentro UN CI.
 *
 * È il disegno che sostituisce «un CI per indirizzo»: la subnet si dichiara,
 * si verifica e si toglie come UNA cosa, e gli esiti per indirizzo stanno
 * qui — riempiti dalla verifica, fonte per fonte nel dettaglio.
 */
function SubnetPubblica({ ci, onChanged }: { ci: CIDetailType; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const tabella =
    (ci.attributes?.reputation_by_address as Record<
      string,
      { esito: string; dettaglio: string }
    >) ?? {}
  const righe = Object.entries(tabella).map(([ip, e]) => ({ ip, esito: e.esito, dettaglio: e.dettaglio }))
  const controllato = ci.attributes?.reputation_checked_at as string | undefined

  async function verifica() {
    setBusy(true)
    setError(null)
    try {
      await api.checkPublicIps(ci.id)
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  function etichetta(esito: string) {
    if (esito === 'segnalato') return t('segnalato')
    if (esito === 'non_verificabile') return t('non verificabile')
    return t('pulito')
  }

  function badge(esito: string) {
    if (esito === 'segnalato') return <span className="badge badge--error">{t('segnalato')}</span>
    if (esito === 'non_verificabile')
      return <span className="badge badge--warn">{t('non verificabile')}</span>
    return <span className="badge badge--ok">{t('pulito')}</span>
  }

  const colonne: Column<(typeof righe)[number]>[] = [
    { key: 'ip', header: t('Indirizzo'), mono: true, value: (r) => r.ip },
    { key: 'esito', header: t('Reputazione'), value: (r) => etichetta(r.esito), render: (r) => badge(r.esito) },
    {
      key: 'dettaglio', header: t('Dettaglio'), value: (r) => r.dettaglio || '',
      render: (r) => <div style={{ maxWidth: 480 }}>{r.dettaglio || '—'}</div>,
    },
  ]

  return (
    <Card title={t('Indirizzi della subnet')}>
      <ErrorBanner error={error} />
      <p className="text-muted" style={{ marginTop: 0 }}>
        {t('La reputazione si verifica indirizzo per indirizzo, su più fonti; il rischio — se qualcosa è segnalato — è uno, su questa subnet.')}
        {controllato && <> {t('Ultima verifica: {quando}.', { quando: formatDateTime(controllato) })}</>}
      </p>
      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={verifica} disabled={busy}>
          {busy ? t('Verifica in corso…') : t('Verifica reputazione')}
        </button>
      </div>
      {righe.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <DataTable
            columns={colonne}
            rows={righe}
            rowKey={(r) => r.ip}
            conteggio={{ nome: t('indirizzi IP') }}
            rowClassName={(r) => (r.esito === 'segnalato' ? 'riga-reperto--alta' : undefined)}
          />
        </div>
      )}
      {righe.length === 0 && (
        <p className="field__hint">{t('Mai verificata: premi «Verifica reputazione» per riempire la tabella.')}</p>
      )}
    </Card>
  )
}


function IpPubblici({ ciId }: { ciId: string }) {
  const indirizzi = useAsync(() => api.publicIps(ciId), [ciId])
  const [testo, setTesto] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const voci = indirizzi.data ?? []
  // Il campo parte dalla lista salvata e da lì in poi è dell'utente: senza il
  // «null finché non tocchi», ogni ricarica gli riscriverebbe sotto le dita.
  const valore = testo ?? voci.map((v) => v.ip).join(', ')

  async function salva() {
    setBusy(true)
    setError(null)
    try {
      await api.setPublicIps(
        ciId,
        (testo ?? valore).split(/[\s,;]+/).filter(Boolean),
      )
      setTesto(null)
      await indirizzi.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function verifica() {
    setBusy(true)
    setError(null)
    try {
      await api.checkPublicIps(ciId)
      await indirizzi.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  function badge(esito: string | null) {
    if (esito === 'segnalato') return <span className="badge badge--error">{t('segnalato')}</span>
    if (esito === 'non_verificabile')
      return <span className="badge badge--warn">{t('non verificabile')}</span>
    if (esito === 'pulito') return <span className="badge badge--ok">{t('pulito')}</span>
    return <span className="text-muted">{t('mai verificata')}</span>
  }

  function etichetta(esito: string | null) {
    if (esito === 'segnalato') return t('segnalato')
    if (esito === 'non_verificabile') return t('non verificabile')
    if (esito === 'pulito') return t('pulito')
    return t('mai verificata')
  }

  const colonne: Column<PublicIp>[] = [
    {
      key: 'ip', header: t('Indirizzo'), mono: true, value: (v) => v.ip,
      // Alla scheda dell'indirizzo: è un CI, con storico e reperti suoi.
      render: (v) => <Link to={`/cis/${v.ci_id}`}>{v.ip}</Link>,
    },
    { key: 'esito', header: t('Reputazione'), value: (v) => etichetta(v.esito), render: (v) => badge(v.esito) },
    { key: 'dettaglio', header: t('Dettaglio'), value: (v) => v.dettaglio || '' },
    {
      key: 'controllato_al', header: t('Controllato'), type: 'date', value: (v) => v.controllato_al ?? '',
      render: (v) => (v.controllato_al ? formatDateTime(v.controllato_al) : '—'),
    },
  ]

  return (
    <Card title={t('IP pubblici')}>
      <ErrorBanner error={error} />
      <p className="text-muted" style={{ marginTop: 0 }}>
        {t('Gli indirizzi attraverso cui l’asset è raggiungibile da Internet: NAT, VIP o indirizzo della sede. Ogni indirizzo è gestito come un CI distinto e può essere condiviso da più asset. La verifica ne controlla la reputazione nelle liste di abuso.')}
      </p>
      <div className="field" style={{ maxWidth: 480 }}>
        <label htmlFor="ip-pubblici">{t('Indirizzi, separati da virgola')}</label>
        <input
          id="ip-pubblici"
          value={valore}
          placeholder="185.60.216.35, 185.60.216.36"
          onChange={(e) => setTesto(e.target.value)}
        />
      </div>
      <div className="rowactions" style={{ textAlign: 'left', marginTop: 12 }}>
        <button onClick={salva} disabled={busy}>
          {busy ? t('Salvataggio…') : t('Salva')}
        </button>
        <button
          className="secondary"
          onClick={verifica}
          disabled={busy || voci.length === 0}
          title={
            voci.length === 0 ? t('Prima dichiara e salva almeno un indirizzo') : undefined
          }
        >
          {busy ? t('Verifica in corso…') : t('Verifica reputazione')}
        </button>
      </div>

      {voci.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <DataTable
            columns={colonne}
            rows={voci}
            rowKey={(v) => v.ci_id}
            conteggio={{ nome: t('indirizzi IP') }}
          />
        </div>
      )}
      <p className="field__hint">
        {t('«Segnalato» indica la presenza di un rischio associato all’indirizzo. «Non verificabile» indica che la verifica non ha prodotto un risultato. Gli indirizzi non più associati ad alcun CI vengono rimossi dall’inventario.')}
      </p>
    </Card>
  )
}

function Lifecycle({ ci, onChanged }: { ci: CIDetailType; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [note, setNote] = useState('')
  const [asking, setAsking] = useState(false)
  const retired = ci.lifecycle_state === 'retired'

  async function apply(state: 'retired' | null) {
    setBusy(true)
    setError(null)
    try {
      await api.setLifecycle(ci.id, state, state === 'retired' ? note : undefined)
      setAsking(false)
      setNote('')
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Stato nel tempo')}>
      <ErrorBanner error={error} />
      <p>
        <LifecycleBadge state={ci.lifecycle_state} />
      </p>
      {/* Il motivo, non solo lo stato: «silente» da solo non dice se a fermarsi
          è stato l'apparato o la raccolta, e le due cose portano a due azioni
          opposte — andare a vedere la macchina, o andare a vedere la sonda. */}
      {ci.lifecycle_reason && <p className="text-muted">{ci.lifecycle_reason}</p>}

      {retired ? (
        <>
          <p className="text-muted">
            {t("Nessun calcolo lo rimette attivo e nessuna scansione lo annulla. Se l'apparato risponde ancora, il motivo qui sopra lo dice.")}
          </p>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <button className="secondary" onClick={() => apply(null)} disabled={busy}>
              {busy ? t('Annullamento…') : t('Annulla la dismissione')}
            </button>
          </div>
        </>
      ) : asking ? (
        <>
          <div className="field" style={{ maxWidth: 420 }}>
            <label htmlFor="dismissione">{t('Perché è stato dismesso')}</label>
            {/* Facoltativo, ma chiesto: fra sei mesi «dismesso» non risponde a
                nessuna domanda, «sostituito dal cluster nuovo di Bergamo» sì. */}
            <input
              id="dismissione"
              value={note}
              maxLength={500}
              placeholder={t('sostituito, restituito al noleggio, rottamato…')}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="rowactions" style={{ textAlign: 'left', marginTop: 12 }}>
            <button onClick={() => apply('retired')} disabled={busy}>
              {busy ? t('Registrazione…') : t('Dichiara dismesso')}
            </button>
            <button className="secondary" onClick={() => setAsking(false)} disabled={busy}>
              {t('Annulla')}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-muted">
            {t("Dismette definitivamente l'asset. Il CI e il relativo storico rimangono nel CMDB.")}
          </p>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <button className="secondary" onClick={() => setAsking(true)} disabled={busy}>
              {t('Dichiara dismesso')}
            </button>
          </div>
        </>
      )}
    </Card>
  )
}

/**
 * Togliere un CI dalla CMDB.
 *
 * # Perché la conferma dice quello che dice
 *
 * Perché il malinteso è prevedibile e costoso: cancellare **non** è dismettere.
 * Un apparato che esiste ancora in rete torna alla prossima scansione, con un
 * identificativo nuovo e senza la propria storia — il gesto si annulla da solo
 * e nel frattempo butta via qualcosa. Chi cancella un CI di solito voleva dire
 * «questo l'abbiamo dismesso», e va detto **prima** che lo faccia.
 *
 * Il bene eventualmente registrato resta, e va detto anche quello: chi teme di
 * perdere il cespite non cancella, e chi voleva perderlo lo cancella dal
 * registro dei beni.
 */
/**
 * Da dove viene questo CI, e chi tiene aggiornato cosa.
 *
 * Tre viste dello stesso fatto, dalla più grossa alla più fine: le **fonti** che
 * l'hanno visto (con quante colonne tiene ciascuna — la misura di quanto una
 * fonte lo conosce, non solo che l'ha sfiorato), i **job** che l'hanno toccato, e
 * la provenienza **per attributo**. È il substrato della discovery intelligente:
 * sapere che un host lo vedono tre metodi diversi è ciò che poi permette di
 * proporne uno solo, puntuale.
 */
/**
 * «Attività» del CI: la linea del tempo unificata di cosa è stato fatto —
 * cambi tecnici (storico) e azioni (dall'audit) — e, in prospettiva, le
 * attività di remediation. Risponde a «cos'è successo, e quando».
 */
function Attivita({ ciId }: { ciId: string }) {
  const stato = useAsync(() => api.ciActivity(ciId), [ciId])
  if (stato.loading && !stato.data) {
    return <Card title={t('Attività')}><Spinner /></Card>
  }
  if (stato.error) {
    return <Card title={t('Attività')}><ErrorBanner error={stato.error} /></Card>
  }
  const voci = stato.data ?? []
  return (
    <Card title={t('Attività')}>
      <p className="text-muted" style={{ marginTop: 0 }}>
        {t('Cosa è stato fatto su questo CI nel tempo: i cambi tecnici rilevati e le azioni eseguite. Dalla più recente.')}
      </p>
      {voci.length === 0 ? (
        <EmptyState title={t('Nessuna attività registrata.')} />
      ) : (
        <ul className="timeline">
          {voci.map((v, i) => (
            <li key={i} className="timeline__item">
              <span className="timeline__when">{formatDateTime(v.at)}</span>
              {v.type === 'action' ? (
                <span>
                  <span className={`chip ${v.outcome === 'ok' ? 'chip--ok' : 'chip--muted'}`}>{t('azione')}</span>{' '}
                  <strong>{v.action}</strong>
                  {v.actor && <span className="text-muted"> — {v.actor}</span>}
                  {v.outcome && v.outcome !== 'ok' && <span className="text-muted"> ({v.outcome})</span>}
                </span>
              ) : (
                <span>
                  <span className="chip chip--muted">{t('modifica')}</span>{' '}
                  <strong>{v.label ?? v.field ?? v.kind}</strong>
                  {(v.old != null || v.new != null) && (
                    <span className="text-muted"> — {String(v.old ?? '—')} → {String(v.new ?? '—')}</span>
                  )}
                  {v.source && <span className="text-muted"> [{v.source}{v.job ? `: ${v.job}` : ''}]</span>}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function Provenienza({ ciId }: { ciId: string }) {
  const prov = useAsync(() => api.ciProvenance(ciId), [ciId])

  if (prov.loading && !prov.data) {
    return (
      <Card title={t('Provenienza')}>
        <Spinner />
      </Card>
    )
  }
  if (prov.error || !prov.data) {
    return (
      <Card title={t('Provenienza')}>
        <ErrorBanner error={prov.error} />
      </Card>
    )
  }

  const p = prov.data

  const colonneFonti: Column<ProvenanceSource>[] = [
    {
      key: 'integration', header: t('Fonte'), value: (s) => s.integration,
      render: (s) => (
        <>
          {s.integration}
          {s.integration === p.source && (
            <span className="badge badge--info" style={{ marginLeft: 6 }}>
              {t('primaria')}
            </span>
          )}
        </>
      ),
    },
    { key: 'attribute_count', header: t('Attributi'), type: 'number', value: (s) => s.attribute_count },
    {
      key: 'first_seen', header: t('Primo rilevamento'), type: 'date', value: (s) => s.first_seen ?? '',
      render: (s) => formatDateTime(s.first_seen),
    },
    {
      key: 'last_seen', header: t('Ultimo rilevamento'), type: 'date', value: (s) => s.last_seen ?? '',
      render: (s) => formatDateTime(s.last_seen),
    },
  ]
  const colonneJob: Column<ProvenanceJob>[] = [
    { key: 'kind', header: t('Tipo'), value: (j) => j.kind },
    {
      key: 'quando', header: t('Quando'), type: 'date', value: (j) => j.started_at ?? j.created_at ?? '',
      render: (j) => formatDateTime(j.started_at ?? j.created_at),
    },
  ]
  const colonneAttributi: Column<ProvenanceAttribute>[] = [
    { key: 'attribute', header: t('Attributo'), value: (a) => attributeLabel(a.attribute) },
    { key: 'source', header: t('Fonte'), value: (a) => a.source ?? '' },
    { key: 'job', header: t('Job'), value: (a) => a.job?.kind ?? '' },
    {
      key: 'at', header: t('Quando'), type: 'date', value: (a) => a.at ?? '',
      render: (a) => formatDateTime(a.at),
    },
  ]

  return (
    <>
      <Card title={t('Provenienza')}>
        <dl className="kv">
          <dt>{t('Origine')}</dt>
          <dd>{p.source ?? '—'}</dd>
          <dt>{t('Ultima sonda')}</dt>
          <dd>{p.appliance ? p.appliance.name : '—'}</dd>
          <dt>{t('Ultimo job')}</dt>
          <dd>{p.last_job ? p.last_job.kind : '—'}</dd>
          <dt>{t('Primo rilevamento')}</dt>
          <dd>{formatDateTime(p.first_seen)}</dd>
          <dt>{t('Ultimo rilevamento')}</dt>
          <dd>{formatDateTime(p.last_seen)}</dd>
        </dl>
      </Card>

      <Card title={`${t('Fonti')} (${p.sources.length})`}>
        <p className="text-muted" style={{ marginTop: 0 }}>
          {t('Indica le fonti che hanno rilevato il CI e il numero di attributi attualmente gestiti da ciascuna fonte, evidenziandone il livello di conoscenza.')}
        </p>
        {p.sources.length === 0 ? (
          <EmptyState title={t('Nessuna fonte registrata')} hint={t('CI creato a mano o prima che si tracciasse la provenienza.')} />
        ) : (
          <DataTable
            columns={colonneFonti}
            rows={p.sources}
            rowKey={(s) => s.integration}
            conteggio={{ nome: t('fonti') }}
          />
        )}
      </Card>

      {p.jobs.length > 0 && (
        <Card title={`${t('Job che l’hanno toccato')} (${p.jobs.length})`}>
          <DataTable
            columns={colonneJob}
            rows={p.jobs}
            rowKey={(j) => j.id}
            conteggio={{ nome: t('job') }}
          />
        </Card>
      )}

      {p.attributes.length > 0 && (
        <Card title={`${t('Provenienza per attributo')} (${p.attributes.length})`}>
          <p className="text-muted" style={{ marginTop: 0 }}>
            {t('Indica la fonte di ciascun attributo del CI, gestendo le priorità in caso di dati provenienti da fonti diverse o discordanti.')}
          </p>
          <DataTable
            columns={colonneAttributi}
            rows={p.attributes}
            rowKey={(a) => a.attribute}
            conteggio={{ nome: t('attributi') }}
          />
        </Card>
      )}
    </>
  )
}

function DeleteCI({ ci }: { ci: CIDetailType }) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function remove() {
    if (
      !window.confirm(
        t('Togliere {nome} dalla CMDB?\n\nSe questo apparato esiste ancora in rete, la prossima scansione lo riporta come oggetto nuovo, senza la sua storia: per una macchina dismessa usa «Dichiara dismesso», più in alto in questa pagina, non la cancellazione.\n\nSpariscono interfacce, software, relazioni e storico. Il bene eventualmente registrato resta, e perde solo il collegamento a questo CI.', { nome: ci.name ?? ci.primary_key }),
      )
    )
      return
    setBusy(true)
    setError(null)
    try {
      await api.deleteCI(ci.id)
      navigate('/cis')
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  return (
    <Card title={t('Togliere dalla CMDB')}>
      <ErrorBanner error={error} />
      <p className="text-muted">
        {t('Da usare per rimuovere record errati, duplicati o relativi ad asset di test; se l’asset è ancora rilevabile in rete, verrà acquisito nuovamente alla scansione successiva.')}
      </p>
      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button className="danger" onClick={remove} disabled={busy}>
          {busy ? t('Rimozione…') : t('Togli dalla CMDB')}
        </button>
      </div>
    </Card>
  )
}

/**
 * Registrazione del CI come bene amministrato.
 *
 * Due passi e non un pulsante: la bozza mostra ciò che la discovery sa già —
 * matricola, costruttore, modello, collocazione — e chiede di completare ciò che
 * solo una persona sa: proprietario e centro di costo. Registrare in un colpo
 * produrrebbe schede con metà dei campi vuoti, che è il modo in cui i registri dei
 * beni diventano inutili.
 */
function PromoteToAsset({ ciId }: { ciId: string }) {
  const navigate = useNavigate()
  const [draft, setDraft] = useState<AssetDraft | null>(null)
  const [existing, setExisting] = useState<string | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function begin() {
    setBusy(true)
    setError(null)
    try {
      const d = await api.assetDraft(ciId)
      setDraft(d)
      setForm({
        asset_tag: d.asset_tag,
        name: d.name ?? '',
        status: d.status,
        category: d.category,
        serial: d.serial ?? '',
        vendor: d.vendor ?? '',
        model: d.model ?? '',
        location: d.location ?? '',
        owner: '',
        cost_center: '',
      })
    } catch (err) {
      // Già promosso: non è un errore da mostrare come tale, è un collegamento.
      if (err instanceof ApiError && err.code === 'already_promoted') {
        setExisting(err.message)
      } else {
        setError(err)
      }
    } finally {
      setBusy(false)
    }
  }

  async function confirm() {
    if (!draft) return
    setBusy(true)
    setError(null)
    try {
      const asset = await api.createAsset({
        ...form,
        ci_id: ciId,
        purchase_price: null,
        attributes: {},
      })
      navigate(`/assets/${asset.id}`)
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  if (existing) {
    return (
      <div className="banner banner--info">
        {t('Questo configuration item è già registrato come bene: {bene}.', { bene: existing })}{' '}
        <Link to="/assets">{t('Vai al registro dei beni →')}</Link>
      </div>
    )
  }

  if (!draft) {
    return (
      <Card title={t('Registra come bene')}>
        <ErrorBanner error={error} />
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Registra l’asset come bene per integrarlo con le informazioni amministrative e contabili non rilevabili dalla rete: proprietà, costo, centro di costo e contratto associato.')}
        </p>
        <button onClick={begin} disabled={busy}>
          {busy ? t('Preparazione…') : t('Registra come bene')}
        </button>
      </Card>
    )
  }

  const set = (key: string) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value })
  const observed = new Set(draft.observed_fields)

  return (
    <Card title={t('Registra come bene')}>
      <ErrorBanner error={error} />
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('I campi contrassegnati arrivano dalla discovery: correggili solo se sono sbagliati. Proprietario e centro di costo li sa solo una persona.')}
      </p>

      <div className="row">
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="p-tag">{t('Cartellino')}</label>
          <input id="p-tag" value={form.asset_tag ?? ''} onChange={set('asset_tag')} />
        </div>
        <div className="field">
          <label htmlFor="p-name">{t('Nome')}</label>
          <input id="p-name" value={form.name ?? ''} onChange={set('name')} />
        </div>
        <div className="field">
          <label htmlFor="p-serial">
            {t('Serial Number')} {observed.has('serial') && <em className="text-muted">· {t('dalla discovery')}</em>}
          </label>
          <input id="p-serial" value={form.serial ?? ''} onChange={set('serial')} />
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="p-vendor">
            {t('Costruttore')} {observed.has('vendor') && <em className="text-muted">· {t('dalla discovery')}</em>}
          </label>
          <input id="p-vendor" value={form.vendor ?? ''} onChange={set('vendor')} />
        </div>
        <div className="field">
          <label htmlFor="p-model">
            {t('Modello')} {observed.has('model') && <em className="text-muted">· {t('dalla discovery')}</em>}
          </label>
          <input id="p-model" value={form.model ?? ''} onChange={set('model')} />
        </div>
        <div className="field">
          <label htmlFor="p-loc">
            {t('Collocazione')}{' '}
            {observed.has('location') && <em className="text-muted">· {t('dalla discovery')}</em>}
          </label>
          <input id="p-loc" value={form.location ?? ''} onChange={set('location')} />
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="p-owner">{t('Proprietario')}</label>
          <input id="p-owner" value={form.owner ?? ''} onChange={set('owner')} />
        </div>
        <div className="field">
          <label htmlFor="p-cc">{t('Centro di costo')}</label>
          <input id="p-cc" value={form.cost_center ?? ''} onChange={set('cost_center')} />
        </div>
      </div>

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={confirm} disabled={busy}>
          {busy ? t('Registrazione…') : t('Conferma registrazione')}
        </button>
        <button className="secondary" onClick={() => setDraft(null)} disabled={busy}>
          {t('Annulla')}
        </button>
      </div>
    </Card>
  )
}


/**
 * Gli attributi che non hanno una riga propria nell'anagrafica.
 *
 * ## Perché non il JSON
 *
 * Prima erano un blocco di JSON indentato. È completo e illeggibile: per sapere
 * su quale datastore sta il secondo disco di una VM bisogna leggere una struttura
 * annidata con le virgolette, e chi apre la scheda di un asset non lo fa per
 * leggere un documento — lo fa per rispondere a una domanda.
 *
 * Il JSON resta comunque raggiungibile, richiuso in fondo: quando un attributo
 * nuovo non ha ancora una resa dedicata è l'unico modo di vederlo, e toglierlo
 * significherebbe nascondere ciò che il connettore ha raccolto.
 */
/**
 * Un valore di attributo, reso per essere LETTO.
 *
 * Le tre forme che rendevano illeggibile la scheda di un host: l'elenco lungo
 * (quaranta porte TCP spalmate su una riga), l'oggetto (JSON grezzo su una
 * riga), la stringa chilometrica. Gettoni per gli elenchi, coppie per gli
 * oggetti, monospazio per il resto tecnico.
 */
function ValoreAttributo({ nome, valore }: { nome: string; valore: unknown }) {
  if (Array.isArray(valore)) {
    if (valore.length === 0) return <>—</>
    const MOSTRATI = 40
    return (
      <span className="chips-dato">
        {valore.slice(0, MOSTRATI).map((v, i) => (
          <span key={i}>{scalar(v)}</span>
        ))}
        {valore.length > MOSTRATI && (
          <span title={valore.slice(MOSTRATI).map((v) => scalar(v)).join(', ')}>
            +{valore.length - MOSTRATI}
          </span>
        )}
      </span>
    )
  }
  if (valore !== null && typeof valore === 'object') {
    const coppie = Object.entries(valore as Record<string, unknown>)
    if (coppie.length === 0) return <>—</>
    return (
      <dl className="kv-annidata">
        {coppie.slice(0, 20).map(([k, v]) => (
          <div key={k}>
            <span className="kv-annidata__nome">{k}</span>
            <span>{scalar(v)}</span>
          </div>
        ))}
        {coppie.length > 20 && (
          <div>
            <span className="kv-annidata__nome">…</span>
            <span className="text-muted">{t('e altre {quante} voci', { quante: coppie.length - 20 })}</span>
          </div>
        )}
      </dl>
    )
  }
  const testo = attributeValue(nome, valore)
  return <span className={typeof valore === 'string' && valore.length > 40 ? 'mono' : undefined}>{testo}</span>
}


/** Una lacuna dichiarata dalla raccolta: cosa non si è letto, e perché. */
interface LacunaInventario {
  section: string
  field: string
  reason: string
  command?: string
}

/**
 * «Dati non letti»: ciò che l'agente (o la raccolta via SSH) non ha potuto
 * leggere, dichiarato invece che taciuto. Vive in `host_inventory.gaps` ed è
 * **l'ultima fotografia**, non un registro: lo stesso dato non letto a ogni
 * giro non si accumula — compare una volta, con il suo motivo. È il posto dove
 * guardare quando in scheda manca un campo, invece del log di sistema del cron.
 */
function DatiNonLetti({ attributes }: { attributes: Record<string, unknown> }) {
  const host = attributes.host_inventory
  const gaps =
    host && typeof host === 'object' && Array.isArray((host as { gaps?: unknown }).gaps)
      ? ((host as { gaps: LacunaInventario[] }).gaps)
      : []
  if (gaps.length === 0) return null

  const colonne: Column<LacunaInventario>[] = [
    { key: 'section', header: t('Sezione'), mono: true, value: (g) => g.section },
    { key: 'field', header: t('Campo'), mono: true, value: (g) => g.field },
    { key: 'reason', header: t('Motivo'), value: (g) => g.reason, render: (g) => <span title={g.command || ''}>{g.reason}</span> },
  ]

  return (
    <Card title={`${t('Dati non letti')} (${gaps.length})`}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Ciò che la raccolta non ha potuto leggere, dichiarato invece che taciuto: un campo assente qui è «non letto», non «non esiste». È l’ultima fotografia, non un registro — lo stesso dato non si accumula a ogni scansione.')}
      </p>
      <DataTable
        columns={colonne}
        rows={gaps}
        rowKey={(g) => `${g.section}.${g.field}`}
      />
    </Card>
  )
}

function OtherAttributes({
  entries,
  sources,
  relations,
}: {
  entries: [string, unknown][]
  sources?: Record<string, { source?: string; at?: string }>
  relations?: CIRelation[]
}) {
  // «Connected on»: the switch ports this device sits on, derived from the
  // real `connected_to` relations (MAC table) — never copied into attributes,
  // so it changes when the device is moved.
  const connectedOn = (relations ?? []).filter(
    (r) => r.rel_type === 'connected_to' && r.direction === 'incoming' && r.peer_id && r.attributes?.local_port,
  )
  if (entries.length === 0 && connectedOn.length === 0) return null

  // Gli elenchi di oggetti — dischi, schede di rete — diventano tabelle: sono la
  // parte per cui il JSON era illeggibile, e sono anche quella che si guarda di
  // più.
  const tables = entries.filter(
    ([, v]) => Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && v[0] !== null,
  ) as [string, Record<string, unknown>[]][]
  const simple = entries.filter(([k]) => !tables.some(([tk]) => tk === k))

  return (
    <>
      {(simple.length > 0 || connectedOn.length > 0) && (
        <Card title={t('Altri attributi')}>
          {/* Ordinati per etichetta: l'ordine di arrivo è quello del JSON, cioè
              nessuno, e su quaranta righe si cerca per nome. */}
          <dl className="kv kv--attributi">
            {connectedOn.length > 0 && (
              <>
                <dt>{t('Connesso su')}</dt>
                <dd>
                  {connectedOn.map((r, i) => (
                    <div key={r.id}>
                      {i > 0 && <br />}
                      <Link to={`/cis/${r.peer_id}`}>{r.peer_name ?? r.peer_id}</Link>
                      {' · '}{t('porta {p}', { p: String(r.attributes.local_port) })}
                      {r.attributes.vlan != null && ` · VLAN ${String(r.attributes.vlan)}`}
                      <span className="text-muted"> · {t('dalla tabella MAC dello switch')}</span>
                    </div>
                  ))}
                </dd>
              </>
            )}
            {[...simple]
              .sort((a, b) => attributeLabel(a[0]).localeCompare(attributeLabel(b[0])))
              .map(([key, value]) => (
                <Fragment key={key}>
                  <dt>{attributeLabel(key)}</dt>
                  <dd>
                    <ValoreAttributo nome={key} valore={value} />
                    {/* Chi ha scritto questo valore. Non è un dettaglio: quando
                        due fonti non concordano, «chi lo dice» è la sola cosa
                        che permette di decidere a quale credere. */}
                    {sources?.[key]?.source && (
                      <span className="text-muted"> · {String(sources[key].source)}</span>
                    )}
                  </dd>
                </Fragment>
              ))}
          </dl>
        </Card>
      )}

      {tables.map(([key, rows]) => {
        // Le colonne sono l'unione delle chiavi presenti: due dischi possono
        // avere campi diversi — uno su un datastore riconosciuto e uno no — e
        // prendere le chiavi del primo perderebbe le colonne del secondo.
        const columns: Column<Record<string, unknown>>[] = Array.from(
          new Set(rows.flatMap((r) => Object.keys(r))),
        ).map((c) => ({
          key: c,
          header: attributeLabel(c),
          mono: c === 'mac' || c === 'file',
          // Numbers stay numbers, so they sort as such.
          value: (row) => (typeof row[c] === 'number' ? (row[c] as number) : senzaTrattino(scalar(row[c]))),
          render: (row) => scalar(row[c]),
        }))
        return (
          <Card key={key} title={`${attributeLabel(key)} (${rows.length})`}>
            <DataTable columns={columns} rows={rows} nomeEsportazione={attributeLabel(key)} />
          </Card>
        )
      })}

      <Card title={t('Dati grezzi')}>
        <details>
          <summary className="field__hint">
            {t('Mostra il JSON completo — serve quando un attributo nuovo non ha ancora una resa dedicata')}
          </summary>
          <pre className="json">{JSON.stringify(Object.fromEntries(entries), null, 2)}</pre>
        </details>
      </Card>
    </>
  )
}

/** Nome leggibile di un tipo di collegamento. */
const RELATION_LABELS: Record<string, () => string> = {
  connected_to: () => t('Collegato a'),
  runs_on: () => t('Gira su'),
  stored_on: () => t('Archiviato su'),
  member_of: () => t('Membro di'),
  depends_on: () => t('Dipende da'),
  talks_to: () => t('Comunica con'),
  hosts: () => t('Ospita'),
  part_of: () => t('Parte di'),
}

/**
 * I collegamenti noti di un CI.
 *
 * ## Perché le colonne non sono fisse
 *
 * Erano quelle di CDP/LLDP — porta locale, porta remota — perché quando la
 * tabella è stata scritta la topologia era l'unica cosa che producesse
 * relazioni. Oggi le producono anche i connettori di virtualizzazione e NetFlow,
 * e una VM che gira su un host non ha nessuna porta: quelle due colonne
 * restavano vuote su quasi tutte le righe. Una colonna sempre vuota non è un
 * dettaglio estetico — è una tabella che sembra funzionare e in cui manca il
 * dato, e fa concludere che la discovery non ha raccolto le porte.
 *
 * Adesso compaiono **solo quando qualche riga le ha**, come per le colonne
 * dell'elenco dei CI.
 *
 * ## Protocollo, occorrenze, direzione
 *
 * Sono le tre cose che la tabella non diceva e che si guardano davvero:
 *
 *   - il **protocollo** distingue un'adiacenza di livello 2 (CDP, LLDP) da una
 *     conversazione osservata sul filo (`tcp/443`), che sono fatti di natura
 *     diversa e non vanno letti allo stesso modo;
 *   - le **occorrenze** distinguono un collegamento stabile da uno visto una
 *     volta e mai più — un ARP di passaggio, una VM migrata, un vicino preso per
 *     sbaglio. Senza il conteggio sono la stessa riga;
 *   - la **direzione** dice chi dipende da chi. Su `depends_on` e `talks_to`
 *     invertirla ribalta il significato, e la tabella la ometteva del tutto.
 */
function Relations({ relations }: { relations: CIRelation[] | null }) {
  const rows = relations ?? []

  const has = (key: string) => rows.some((r) => r.attributes?.[key] != null)
  const showLocalPort = has('local_port')
  const showRemotePort = has('remote_port')
  // Le occorrenze si mostrano solo se qualcuna supera uno: una colonna di «1»
  // occupa spazio senza distinguere niente.
  const showObservations = rows.some((r) => (r.observations ?? 1) > 1)

  const direzione = (r: CIRelation) => (r.direction === 'outgoing' ? `→ ${t('uscente')}` : `← ${t('entrante')}`)
  const colonne: Column<CIRelation>[] = [
    {
      key: 'peer', header: t('Dispositivo'), value: (r) => r.peer_name ?? '',
      render: (r) => (r.peer_id ? <Link to={`/cis/${r.peer_id}`}>{r.peer_name}</Link> : r.peer_name ?? '—'),
    },
    { key: 'rel_type', header: t('Collegamento'), value: (r) => RELATION_LABELS[r.rel_type]?.() ?? r.rel_type.replace(/_/g, ' ') },
    {
      key: 'direction', header: t('Direzione'), value: direzione,
      render: (r) => (
        <span title={r.direction === 'outgoing' ? t('da questo CI') : t('verso questo CI')}>{direzione(r)}</span>
      ),
    },
    { key: 'protocol', header: t('Protocollo'), mono: true, value: (r) => senzaTrattino(relationProtocol(r)) },
    ...(showLocalPort
      ? [{ key: 'local_port', header: t('Porta locale'), mono: true, value: (r: CIRelation) => senzaTrattino(scalar(r.attributes?.local_port)) }]
      : []),
    ...(showRemotePort
      ? [{ key: 'remote_port', header: t('Porta remota'), mono: true, value: (r: CIRelation) => senzaTrattino(scalar(r.attributes?.remote_port)) }]
      : []),
    ...(showObservations
      ? [{ key: 'observations', header: t('Occorrenze'), type: 'number' as const, value: (r: CIRelation) => r.observations ?? 1 }]
      : []),
    { key: 'via', header: t('Rilevato tramite'), mono: true, value: (r) => r.via ?? '' },
    {
      key: 'last_seen', header: t('Ultimo rilevamento'), type: 'date', value: (r) => r.last_seen ?? '',
      render: (r) => formatDateTime(r.last_seen),
    },
  ]

  return (
    <Card title={t('Collegamenti ({n})', { n: rows.length })}>
      {rows.length === 0 ? (
        <EmptyState
          title={t('Nessun collegamento noto')}
          hint={t('Le adiacenze si popolano con una discovery che raccoglie CDP/LLDP, con i connettori di virtualizzazione o con NetFlow.')}
        />
      ) : (
        <DataTable
          columns={colonne}
          rows={rows}
          rowKey={(r) => r.id}
          conteggio={{ nome: t('collegamenti') }}
        />
      )}
    </Card>
  )
}

/**
 * Il protocollo di un collegamento, che non è la fonte da cui lo sappiamo.
 *
 * `via` dice **come** l'abbiamo saputo: `cdp`, `lldp`, `netflow`, `vmware_api`.
 * Per CDP e LLDP le due cose coincidono — sono protocolli di adiacenza — ma per
 * una relazione letta da un'API non esiste nessun protocollo di rete, e scrivere
 * «vmware_api» sotto «Protocollo» sarebbe una risposta a una domanda diversa.
 */
function relationProtocol(r: CIRelation): string {
  const protocol = r.attributes?.protocol
  if (typeof protocol === 'string' && protocol) {
    const port = r.attributes?.port
    return port != null ? `${protocol}/${port}` : protocol
  }
  const via = (r.via ?? '').toLowerCase()
  if (via === 'cdp' || via === 'lldp') return via.toUpperCase()
  return '—'
}

/**
 * Gli indirizzi aggiuntivi di un CI: VIP, alias di servizio, secondari.
 *
 * Ogni riga (IP, FQDN, funzione, note) crea un CI `network_address` legato a
 * questo con `alias_of`; l'IP e l'FQDN entrano nell'identità, così il traffico
 * verso il VIP si riconcilia con quell'oggetto invece di comparire come uno
 * sconosciuto. È un CI a sé — ci si arriva dal link — perché un VIP ha una vita
 * propria: ci arriva del traffico, può spostarsi con un failover.
 */
/** I sistemi operativi come li codifica DeviceManager (OPERATIONSYSTEM): un
 *  numero, che sui dati veri era illeggibile («7» per un ESXi). I codici sono
 *  quelli documentati Huawei; uno sconosciuto resta com'è, non si inventa. */
const OS_ARRAY: Record<string, string> = {
  '0': 'Linux', '1': 'Windows', '2': 'Solaris', '3': 'HP-UX', '4': 'AIX',
  '5': 'XenServer', '6': 'Mac OS', '7': 'VMware ESXi', '8': 'Linux VIS',
  '9': 'Windows Server 2012', '10': 'Oracle VM', '11': 'OpenVMS',
}

function osArray(codice: unknown): string {
  const chiave = codice == null ? '' : String(codice)
  return OS_ARRAY[chiave] ?? (chiave || '—')
}

// I fatti dei protocolli sul filo per gli indirizzi del CI: User-Agent e
// Server HTTP, certificato e nomi TLS, banner SSH, nome/dominio/versione
// Windows da SMB, AE title DICOM, applicazioni HL7, SIP, WS-Discovery. La
// card compare solo se la sonda ha sentito qualcosa su uno dei suoi IP.
function VistoSulFiloCard({ ciId }: { ciId: string }) {
  const fatti = useAsync(() => api.ciWireSightings(ciId), [ciId])
  const righe = fatti.data ?? []
  if (righe.length === 0) return null
  const colonne: Column<HostSighting>[] = [
    {
      key: 'ip', header: 'IP', mono: true, value: (r) => (r.vlan != null ? `${r.ip} VLAN ${r.vlan}` : r.ip),
      render: (r) => <>{r.ip}{r.vlan != null && <small className="text-muted"> VLAN {r.vlan}</small>}</>,
    },
    {
      key: 'kind', header: t('Tipo riconosciuto'),
      value: (r) => [r.kind ?? '', r.medical ? t('medicale') : ''].filter(Boolean).join(' '),
      render: (r) => (
        <>
          {r.kind ?? <span className="text-muted">—</span>}
          {r.medical && <> <span className="badge badge--warn" title={t('Parla DICOM o HL7: è un’apparecchiatura o un sistema sanitario.')}>{t('medicale')}</span></>}
        </>
      ),
    },
    {
      key: 'detto', header: t('Cosa ha detto sul filo'), value: (r) => riassuntoFilo(r).join(' · '),
      render: (r) => riassuntoFilo(r).map((p, i) => <div key={i}><small>{p}</small></div>),
    },
    {
      key: 'last_seen', header: t('Ultima vista'), type: 'date', mono: true, value: (r) => r.last_seen ?? '',
      render: (r) => <small>{r.last_seen ? formatDateTime(r.last_seen) : '—'}</small>,
    },
  ]
  return (
    <Card title={t('Visto sul filo')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Ciò che questa macchina ha detto di sé nel traffico visto dalla sonda (porta mirror o promiscuo): protocolli unicast, per indirizzo.')}
      </p>
      <DataTable columns={colonne} rows={righe} rowKey={(r) => r.ip} conteggio={{ nome: t('indirizzi IP') }} />
    </Card>
  )
}

// La scheda di un bilanciatore: i servizi virtuali (VIP:porta, protocollo,
// bilanciamento, persistenza, stato, connessioni attive) con sotto i server
// reali che li servono, e i certificati con la scadenza. I dati vengono dal
// driver SNMP del costruttore (oggi Kemp LoadMaster); le righe «slave of»
// sono i sotto-servizi (SubVS) del Kemp e si mostrano come tali.
function LoadBalancerCard({ attributes }: { attributes: Record<string, unknown> }) {
  const lista = (chiave: string): Record<string, unknown>[] => {
    const v = attributes[chiave]
    return Array.isArray(v) ? (v as Record<string, unknown>[]) : []
  }
  const servizi = lista('lb_virtual_services')
  const reali = lista('lb_real_servers')
  const certificati = lista('lb_certificates')
  const testo = (v: unknown): string => (v == null || v === '' ? '—' : String(v))
  const realiDi = (vsId: unknown) => reali.filter((r) => String(r['vs_id'] ?? '') === String(vsId ?? ''))
  const classeStato = (stato: unknown): string => {
    const s = String(stato ?? '')
    if (s === 'in servizio') return 'badge badge--ok'
    if (s === 'disabilitato' || s === 'fuori servizio') return 'badge'
    if (s === 'guasto' || s === 'errore') return 'badge badge--error'
    return 'badge badge--warn'
  }
  const inServizio = servizi.filter((s) => s['state'] === 'in servizio').length
  const realiGiu = reali.filter((r) => r['state'] === 'guasto' || r['state'] === 'fuori servizio').length
  // La scadenza di un certificato: rossa se passata, gialla entro 30 giorni.
  const scadenza = (iso: unknown): { testo: string; classe: string } => {
    const s = String(iso ?? '')
    const quando = s ? new Date(s) : null
    if (!quando || Number.isNaN(quando.getTime())) return { testo: '—', classe: '' }
    const giorni = Math.round((quando.getTime() - Date.now()) / 86400000)
    const classe = giorni < 0 ? 'badge badge--error' : giorni <= 30 ? 'badge badge--warn' : ''
    return { testo: formatDateTime(s), classe }
  }
  const scaduti = certificati.filter((c) => scadenza(c['not_after']).classe === 'badge badge--error').length
  const kpi: [string, string][] = []
  if (servizi.length) kpi.push([`${inServizio}/${servizi.length}`, t('Servizi virtuali in servizio')])
  if (reali.length) kpi.push([String(reali.length - realiGiu) + '/' + String(reali.length), t('Server reali in servizio')])
  if (certificati.length) kpi.push([String(certificati.length), t('Certificati')])
  if (scaduti) kpi.push([String(scaduti), t('Certificati scaduti')])

  type Riga = Record<string, unknown>
  // The VIP as shown: a SubVS points to its parent («slave of …»).
  const vip = (s: Riga): string => {
    const ip = String(s['ip'] ?? '')
    const porta = String(s['port'] ?? '')
    if (ip.startsWith('slave of')) return ip.replace('slave of', '→')
    return porta && porta !== '0' ? `${ip}:${porta}` : ip
  }
  const numeroO = (v: unknown): number | '' => {
    const n = v == null || v === '' ? NaN : Number(v)
    return Number.isFinite(n) ? n : ''
  }
  const colonneServizi: Column<Riga>[] = [
    {
      key: 'name', header: t('Nome'),
      value: (s) => String(s['name'] ?? ''),
      render: (s) => (
        <span>
          {String(s['ip'] ?? '').startsWith('slave of') && <small className="text-muted">{t('sotto-servizio')} · </small>}
          {testo(s['name'])}
        </span>
      ),
    },
    { key: 'vip', header: 'VIP', mono: true, value: (s) => vip(s) },
    { key: 'protocol', header: t('Protocollo'), value: (s) => [s['protocol'], s['type']].filter(Boolean).join(' / ') },
    { key: 'scheduling', header: t('Bilanciamento'), mono: true, value: (s) => String(s['scheduling'] ?? '') },
    { key: 'persistence', header: t('Persistenza'), mono: true, value: (s) => String(s['persistence'] ?? '') },
    {
      key: 'state', header: t('Stato'), value: (s) => String(s['state'] ?? ''),
      render: (s) => <span className={classeStato(s['state'])}>{testo(s['state'])}</span>,
    },
    { key: 'active_connections', header: t('Connessioni'), type: 'number', mono: true, value: (s) => numeroO(s['active_connections']) },
    {
      key: 'reali', header: t('Server reali'),
      value: (s) => realiDi(s['id']).map((r) => `${testo(r['ip'])}:${testo(r['port'])} ${testo(r['state'])}`).join(' '),
      render: (s) => {
        const figli = realiDi(s['id'])
        return figli.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          figli.map((r, j) => (
            <div key={j} className="mono">
              {testo(r['ip'])}:{testo(r['port'])}
              {' '}<span className={classeStato(r['state'])}>{testo(r['state'])}</span>
              {r['weight'] != null && <small className="text-muted"> {t('peso')} {testo(r['weight'])}</small>}
            </div>
          ))
        )
      },
    },
  ]
  const colonneCertificati: Column<Riga>[] = [
    { key: 'name', header: t('Nome'), value: (c) => String(c['name'] ?? '') },
    {
      key: 'issuer', header: t('Emittente'), value: (c) => String(c['issuer'] ?? ''),
      render: (c) => <span title={String(c['issuer_dn'] ?? '')}>{testo(c['issuer'])}</span>,
    },
    {
      key: 'not_before', header: t('Valido dal'), type: 'date', mono: true, value: (c) => String(c['not_before'] ?? ''),
      render: (c) => scadenza(c['not_before']).testo,
    },
    {
      key: 'not_after', header: t('Scade'), type: 'date', mono: true, value: (c) => String(c['not_after'] ?? ''),
      render: (c) => {
        const fine = scadenza(c['not_after'])
        return fine.classe ? <span className={fine.classe}>{fine.testo}</span> : fine.testo
      },
    },
    {
      key: 'serial', header: t('Seriale'), mono: true, value: (c) => String(c['serial'] ?? ''),
      render: (c) => <small>{testo(c['serial'])}</small>,
    },
  ]

  return (
    <Card title={t('Bilanciamento')}>
      {kpi.length > 0 && (
        <div className="venv-kpi">
          {kpi.map(([valore, etichetta]) => (
            <div key={etichetta} className="venv-kpi__tile">
              <span className="venv-kpi__value">{valore}</span>
              <span className="venv-kpi__label">{etichetta}</span>
            </div>
          ))}
        </div>
      )}
      {servizi.length > 0 && (
        <div className="field">
          <label>{t('Servizi virtuali')}</label>
          <DataTable columns={colonneServizi} rows={servizi}
            rowClassName={(s) => (String(s['ip'] ?? '').startsWith('slave of') ? 'riga-attenuata' : undefined)} />
        </div>
      )}
      {certificati.length > 0 && (
        <div className="field">
          <label>{t('Certificati')}</label>
          <DataTable columns={colonneCertificati} rows={certificati} conteggio={{ nome: t('certificati') }} />
        </div>
      )}
    </Card>
  )
}

// La scheda di un sistema di storage: attributi propri, diversi da un server o
// da uno switch. Via SNMP un OceanStor espone le **mappature** (quale gruppo di
// LUN è servito a quale gruppo di host, con quali porte); dischi, pool e LUN
// con le capacità arrivano dall'inventario REST del costruttore
// («Huawei OceanStor»). Le tabelle compaiono solo se popolate.
function StorageCard({ attributes }: { attributes: Record<string, unknown> }) {
  const lista = (chiave: string): Record<string, unknown>[] => {
    const v = attributes[chiave]
    return Array.isArray(v) ? (v as Record<string, unknown>[]) : []
  }
  const mappings = lista('storage_mappings')
  const disks = lista('storage_disks')
  const pools = lista('storage_pools')
  const luns = lista('storage_luns')
  const hosts = lista('storage_hosts')
  const initiators = lista('storage_initiators')
  // Le porte ethernet NON hanno una tabella qui: sono già le «Interfacce di
  // rete» del CI, poco sotto — due tabelle uguali sulla stessa pagina.

  const testo = (r: Record<string, unknown>, k: string): string => {
    const v = r[k]
    return v == null || v === '' ? '—' : String(v)
  }
  // Le capacità arrivano in byte (numero o stringa, a seconda del firmware):
  // si mostrano leggibili, e ciò che non è un numero resta un trattino.
  const capacita = (r: Record<string, unknown>, k: string): string => {
    const n = Number(r[k])
    return Number.isFinite(n) && n > 0 ? formatBytes(n) : '—'
  }
  const somma = (righe: Record<string, unknown>[], k: string): number =>
    righe.reduce((tot, r) => {
      const n = Number(r[k])
      return Number.isFinite(n) && n > 0 ? tot + n : tot
    }, 0)

  type Colonna = [string, string, ((r: Record<string, unknown>, k: string) => string)?]
  // Capacities show as «1.2 TB» but sort by their bytes: as text «900 GB»
  // would come after «1.2 TB».
  const inByte = (resa: Colonna[2]) => resa === capacita || resa === allocataLun
  const tabella = (righe: Record<string, unknown>[], colonne: Colonna[], nome: string) => (
    <DataTable
      rows={righe}
      nomeEsportazione={nome}
      columns={colonne.map(([k, et, resa]): Column<Record<string, unknown>> => ({
        key: k,
        header: et,
        mono: true,
        type: inByte(resa) ? 'number' : undefined,
        value: (r) => {
          const mostrato = (resa ?? testo)(r, k)
          if (mostrato === '—') return ''
          return inByte(resa) ? Number(r[k]) : mostrato
        },
        render: (r) => (resa ?? testo)(r, k),
      }))}
    />
  )

  // La percentuale d'uso di un pool, quando totale e usato ci sono entrambi.
  const usoPool = (r: Record<string, unknown>): string => {
    const tot = Number(r['capacita_totale_bytes'])
    const usata = Number(r['capacita_usata_bytes'])
    if (!Number.isFinite(tot) || tot <= 0 || !Number.isFinite(usata) || usata < 0) return '—'
    return `${Math.round((usata / tot) * 100)}%`
  }

  // DeviceManager: ALLOCTYPE 0 = thick, 1 = thin.
  const allocazioneLun = (r: Record<string, unknown>): string => {
    const v = String(r['tipo_allocazione'] ?? '')
    return v === '1' ? 'Thin' : v === '0' ? 'Thick' : '—'
  }
  const allocataLun = (r: Record<string, unknown>): string => {
    const allocata = Number(r['allocata_bytes'])
    const cap = Number(r['capacita_bytes'])
    if (!Number.isFinite(allocata) || allocata < 0 || r['allocata_bytes'] == null) return '—'
    const perc = Number.isFinite(cap) && cap > 0 ? ` (${Math.round((allocata / cap) * 100)}%)` : ''
    return `${formatBytes(allocata)}${perc}`
  }

  // Un riquadro per ogni fatto presente: su un array letto solo via SNMP
  // compaiono le mappature, non «0 dischi» che sembrerebbe un fatto. La
  // capacità complessiva (e quanta è usata) è la prima domanda su una SAN,
  // quindi sta nei riquadri e non in fondo a una tabella.
  const kpi: [string, string][] = []
  if (mappings.length) kpi.push([String(mappings.length), t('Mappature')])
  if (disks.length) kpi.push([String(disks.length), t('Dischi')])
  if (pools.length) kpi.push([String(pools.length), t('Pool di storage')])
  if (luns.length) kpi.push([String(luns.length), t('LUN')])
  if (hosts.length) kpi.push([String(hosts.length), t('Host registrati')])
  const totalePool = somma(pools, 'capacita_totale_bytes')
  const usataPool = somma(pools, 'capacita_usata_bytes')
  if (totalePool > 0) kpi.push([formatBytes(totalePool), t('Capacità')])
  if (usataPool > 0) {
    kpi.push([
      `${formatBytes(usataPool)} (${Math.round((usataPool / Math.max(totalePool, usataPool)) * 100)}%)`,
      t('Usata'),
    ])
  }

  return (
    <Card title={t('Storage')}>
      {kpi.length > 0 && (
        <div className="venv-kpi">
          {kpi.map(([valore, etichetta]) => (
            <div key={etichetta} className="venv-kpi__tile">
              <span className="venv-kpi__value">{valore}</span>
              <span className="venv-kpi__label">{etichetta}</span>
            </div>
          ))}
        </div>
      )}
      {mappings.length > 0 && (
        <div className="field">
          <label>{t('Mappature (LUN → host)')}</label>
          {tabella(mappings, [
            ['gruppo_lun', t('Gruppo LUN')],
            ['gruppo_host', t('Gruppo host')],
            ['gruppo_porte', t('Gruppo porte')],
          ], t('Mappature (LUN → host)'))}
        </div>
      )}
      {pools.length > 0 && (
        <div className="field">
          <label>{t('Pool di storage')}</label>
          {tabella(pools, [
            ['id', 'ID'],
            ['nome', t('Nome')],
            ['capacita_totale_bytes', t('Capacità'), capacita],
            ['capacita_usata_bytes', t('Usata'), capacita],
            ['uso', t('Uso'), (r) => usoPool(r)],
          ], t('Pool di storage'))}
        </div>
      )}
      {luns.length > 0 && (
        <div className="field">
          <label>{t('LUN')}</label>
          {tabella(luns, [
            ['id', 'ID'],
            ['nome', t('Nome')],
            // Il WWN è l'identità della LUN vista dagli host (il device
            // `naa.<wwn>`): è quello che si confronta quando si cerca chi
            // monta cosa.
            ['wwn', 'WWN'],
            ['pool', t('Pool')],
            ['tipo_allocazione', t('Allocazione'), allocazioneLun],
            ['capacita_bytes', t('Capacità'), capacita],
            // L'occupato vero: su una LUN thin la capacità è una promessa, e
            // lo spazio preso dal pool è questo. Sulle thick coincide.
            ['allocata_bytes', t('Allocata'), allocataLun],
          ], t('LUN'))}
        </div>
      )}
      {hosts.length > 0 && (
        <div className="field">
          <label>{t('Host registrati')}</label>
          {tabella(hosts, [
            ['nome', t('Nome')],
            ['ip', t('Indirizzo')],
            ['os', 'OS', (r) => osArray(r['os'])],
          ], t('Host registrati'))}
        </div>
      )}
      {initiators.length > 0 && (
        <div className="field">
          <label>{t('Initiator')}</label>
          {tabella(initiators, [
            ['id', 'IQN / WWPN'],
            ['host', 'Host'],
            ['tipo', t('Tipo')],
          ], t('Initiator'))}
        </div>
      )}
      {disks.length > 0 && (
        <div className="field">
          <label>{t('Dischi')}</label>
          {tabella(disks, [
            ['id', 'ID'],
            ['modello', t('Modello')],
            ['seriale', t('Numero di serie')],
            ['capacita_bytes', t('Capacità'), capacita],
          ], t('Dischi'))}
        </div>
      )}
      {!disks.length && !pools.length && !luns.length && (
        <p className="field__hint">
          {t('Dischi, pool e LUN con le capacità arrivano dall’inventario «Huawei OceanStor» (REST): lancia quella discovery su questo array.')}
        </p>
      )}
    </Card>
  )
}

function IndirizziAggiuntivi({ ciId }: { ciId: string }) {
  const elenco = useAsync(() => api.ciAddresses(ciId), [ciId])
  const [address, setAddress] = useState('')
  const [fqdn, setFqdn] = useState('')
  const [funzione, setFunzione] = useState('')
  const [nota, setNota] = useState('')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  async function aggiungi() {
    if (!address.trim()) return
    setBusy(true)
    setErrore(null)
    try {
      await api.addCiAddress(ciId, {
        address: address.trim(), fqdn: fqdn.trim(),
        function: funzione.trim(), note: nota.trim(),
      })
      setAddress(''); setFqdn(''); setFunzione(''); setNota('')
      await elenco.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function rimuovi(id: string) {
    if (!window.confirm(t('Togliere questo indirizzo dal CI? Non si può annullare: andrà aggiunto di nuovo.'))) return
    setErrore(null)
    try {
      await api.removeCiAddress(ciId, id)
      await elenco.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  const righe = elenco.data ?? []
  const colonne: Column<ExtraAddress>[] = [
    {
      key: 'address', header: t('Indirizzo'), mono: true, value: (r) => r.address,
      render: (r) => <Link to={`/cis/${r.id}`}>{r.address}</Link>,
    },
    { key: 'fqdn', header: t('FQDN'), mono: true, value: (r) => r.fqdn || '' },
    { key: 'function', header: t('Funzione'), value: (r) => r.function || '' },
    { key: 'note', header: t('Note'), value: (r) => r.note || '', render: (r) => <span className="muted">{r.note || '—'}</span> },
    {
      key: 'azioni', header: '', sort: false, searchable: false, align: 'right',
      render: (r) => (
        <button type="button" className="secondary dob-azione"
                onClick={() => void rimuovi(r.id)}>
          {t('Rimuovi')}
        </button>
      ),
    },
  ]

  return (
    <Card title={t('Indirizzi aggiuntivi')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('VIP, alias di servizio o indirizzi secondari di questo oggetto. Ognuno diventa un CI a sé, legato a questo: l’indirizzo e l’FQDN entrano nell’identità, così il traffico verso il VIP si riconcilia con l’oggetto giusto.')}
      </p>

      {righe.length > 0 && (
        <DataTable columns={colonne} rows={righe} rowKey={(r) => r.id} conteggio={{ nome: t('indirizzi IP') }} />
      )}

      <div className="indirizzi-form">
        <input value={address} onChange={(e) => setAddress(e.target.value)}
               className="mono" placeholder={t('Indirizzo IP')} aria-label={t('Indirizzo IP')} />
        <input value={fqdn} onChange={(e) => setFqdn(e.target.value)}
               className="mono" placeholder={t('vip.azienda.it')} aria-label={t('FQDN')} />
        <input value={funzione} onChange={(e) => setFunzione(e.target.value)}
               placeholder={t('es. VIP del bilanciatore')} aria-label={t('Funzione')} />
        <input value={nota} onChange={(e) => setNota(e.target.value)}
               placeholder={t('Note')} aria-label={t('Note')} />
        <button type="button" onClick={aggiungi} disabled={busy || !address.trim()}>
          {t('Aggiungi')}
        </button>
      </div>
      <ErrorBanner error={errore} />
    </Card>
  )
}

// I nomi in più con cui l'oggetto risponde: alias di servizio, CNAME, vecchi
// nomi. A differenza degli indirizzi aggiuntivi non sono CI a sé — un nome non
// è un oggetto — ma un attributo dichiarato su questo CI.
function FqdnAggiuntivi({ ciId }: { ciId: string }) {
  const elenco = useAsync(() => api.ciFqdns(ciId), [ciId])
  const [fqdn, setFqdn] = useState('')
  const [funzione, setFunzione] = useState('')
  const [nota, setNota] = useState('')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  async function aggiungi() {
    if (!fqdn.trim()) return
    setBusy(true)
    setErrore(null)
    try {
      await api.addCiFqdn(ciId, {
        fqdn: fqdn.trim(), function: funzione.trim(), note: nota.trim(),
      })
      setFqdn(''); setFunzione(''); setNota('')
      await elenco.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function rimuovi(id: string) {
    if (!window.confirm(t('Togliere questo nome dal CI? Non si può annullare: andrà aggiunto di nuovo.'))) return
    setErrore(null)
    try {
      await api.removeCiFqdn(ciId, id)
      await elenco.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  const righe = elenco.data ?? []
  const colonne: Column<ExtraFqdn>[] = [
    { key: 'fqdn', header: t('FQDN'), mono: true, value: (r) => r.fqdn },
    { key: 'function', header: t('Funzione'), value: (r) => r.function || '' },
    { key: 'note', header: t('Note'), value: (r) => r.note || '', render: (r) => <span className="muted">{r.note || '—'}</span> },
    {
      key: 'azioni', header: '', sort: false, searchable: false, align: 'right',
      render: (r) => (
        <button type="button" className="secondary dob-azione"
                onClick={() => void rimuovi(r.id)}>
          {t('Rimuovi')}
        </button>
      ),
    },
  ]

  return (
    <Card title={t('FQDN aggiuntivi')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Alias e CNAME identificano i nomi DNS alternativi associati all’oggetto, utilizzabili per la risoluzione e l’accesso oltre al nome DNS principale.')}
      </p>

      {righe.length > 0 && (
        <DataTable columns={colonne} rows={righe} rowKey={(r) => r.id} conteggio={{ nome: t('FQDN') }} />
      )}

      <div className="indirizzi-form">
        <input value={fqdn} onChange={(e) => setFqdn(e.target.value)}
               className="mono" placeholder={t('alias.azienda.it')} aria-label={t('FQDN')} />
        <input value={funzione} onChange={(e) => setFunzione(e.target.value)}
               placeholder={t('es. nome pubblico del servizio')} aria-label={t('Funzione')} />
        <input value={nota} onChange={(e) => setNota(e.target.value)}
               placeholder={t('Note')} aria-label={t('Note')} />
        <button type="button" onClick={aggiungi} disabled={busy || !fqdn.trim()}>
          {t('Aggiungi')}
        </button>
      </div>
      <ErrorBanner error={errore} />
    </Card>
  )
}

// ------------------------------------- Network Security: il rating ntopng live

/**
 * La sezione «Network Security» del CI: il rating di ntopng in **tempo reale**.
 *
 * Non legge la CMDB — interroga ntopng al momento, attraverso la sonda (il
 * Control Plane non lo raggiunge). Punteggio, allarmi, throughput e con chi
 * parla adesso: dati volatili, chiesti quando si apre la sezione, non salvati.
 */
function SezioneNetworkSecurity({ ciId }: { ciId: string }) {
  const dati = useAsync(() => api.ciNetworkSecurity(ciId), [ciId])
  const d = dati.data

  return (
    <Card
      title={t('Network Security (ntopng)')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Interrogo…') : t('Aggiorna')}
        </button>
      }
    >
      <p className="field__hint">
        {t('Dati in tempo reale da ntopng, chiesti alla sonda al momento: non sono salvati.')}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('ntopng non è interrogabile.')}</p>
      ) : !d.found ? (
        <EmptyState
          title={t('ntopng non conosce questo host')}
          hint={t('Nessun traffico osservato di recente per {ip}.', { ip: d.ip ?? '' })}
        />
      ) : (
        <>
          <div className="ci-sommario">
            <span className="ci-sommario__item">
              <span className="ci-sommario__k">{t('Score')}</span>{numeroOppureTrattino(d.rating?.score)}
            </span>
            <span className="ci-sommario__item">
              <span className="ci-sommario__k">{t('Allarmi')}</span>{numeroOppureTrattino(d.rating?.num_alerts)}
            </span>
            <span className="ci-sommario__item">
              <span className="ci-sommario__k">{t('Throughput')}</span>{fmtBps(d.rating?.throughput_bps)}
            </span>
            {d.rating?.blacklisted && <span className="badge badge--error">{t('In blacklist')}</span>}
            {d.rating?.local_network && (
              <span className="ci-sommario__item">
                <span className="ci-sommario__k">{t('Rete')}</span>
                <span className="mono">{d.rating.local_network}</span>
              </span>
            )}
          </div>

          <h4 style={{ margin: '14px 0 6px', fontSize: 14 }}>
            {t('Con chi parla fuori')} ({d.talkers?.length ?? 0})
          </h4>
          {(d.talkers?.length ?? 0) === 0 ? (
            <p className="muted">
              {t('Nessun interlocutore critico (verso l’esterno o in blacklist) al momento.')}
              {(d.talkers_interni ?? 0) > 0 &&
                ' ' + t('{n} conversazioni interne non mostrate.', { n: d.talkers_interni })}
            </p>
          ) : (
            <>
            <TrafficBars
              data={d.talkers!.slice(0, 8).map((peer, i) => ({
                key: `${peer.peer_ip}-${i}`,
                label: peer.peer_name || peer.peer_ip,
                bytes: peer.bytes ?? 0,
              }))}
            />
            <DataTable
              columns={colonneInterlocutori()}
              rows={d.talkers!}
              rowKey={(peer, i) => `${peer.peer_ip}-${i}`}
            />
            </>
          )}
          {(d.talkers?.length ?? 0) > 0 && (d.talkers_interni ?? 0) > 0 && (
            <p className="field__hint">
              {t('{n} conversazioni interne non mostrate.', { n: d.talkers_interni })}
            </p>
          )}
          {d.queried_at && (
            <p className="field__hint">
              {t('Interrogato: {quando}', { quando: formatDateTime(d.queried_at) })}
            </p>
          )}
        </>
      )}
    </Card>
  )
}

/** The columns of the ntopng talkers (a function: `t()` reads the current language). */
function colonneInterlocutori(): Column<NtopngTalker>[] {
  return [
    {
      key: 'peer', header: t('Interlocutore'),
      value: (peer) => [peer.peer_ip, peer.peer_name ?? '', peer.blacklisted ? t('blacklist') : ''].filter(Boolean).join(' '),
      render: (peer) => (
        <>
          <span className="mono">{peer.peer_ip}</span>
          {peer.peer_name && <div className="muted">{peer.peer_name}</div>}
          {peer.blacklisted && <span className="badge badge--error">{t('blacklist')}</span>}
        </>
      ),
    },
    {
      key: 'dove', header: t('Dove'),
      value: (peer) => (peer.esterno ? `${t('fuori')}${peer.country ? ` · ${peer.country}` : ''}` : t('dentro')),
      render: (peer) =>
        peer.esterno ? (
          <span className="badge badge--warn">
            {t('fuori')}{peer.country ? ` · ${peer.country}` : ''}
          </span>
        ) : (
          <span className="badge">{t('dentro')}</span>
        ),
    },
    { key: 'direzione', header: t('Direzione'), value: (peer) => (peer.direzione === 'uscente' ? t('uscente') : t('entrante')) },
    { key: 'protocol', header: t('Protocollo'), value: (peer) => peer.protocol ?? '' },
    { key: 'port', header: t('Porta'), mono: true, value: (peer) => peer.port ?? '' },
    {
      key: 'bytes', header: t('Volume'), type: 'number', value: (peer) => peer.bytes ?? 0,
      render: (peer) => fmtBytesRete(peer.bytes),
    },
  ]
}

function numeroOppureTrattino(n: number | null | undefined): string {
  return typeof n === 'number' ? String(n) : '—'
}

function fmtBps(bps: number | null | undefined): string {
  if (typeof bps !== 'number' || bps <= 0) return '0'
  const unita = ['bps', 'Kbps', 'Mbps', 'Gbps']
  let v = bps
  let i = 0
  while (v >= 1000 && i < unita.length - 1) {
    v /= 1000
    i++
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${unita[i]}`
}

function fmtBytesRete(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || bytes <= 0) return '0'
  const unita = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = bytes
  let i = 0
  while (v >= 1024 && i < unita.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${unita[i]}`
}

// ------------------------------------------- Wazuh: agente e conformità (live)

/**
 * La sezione «Wazuh» del CI: stato dell'agente e conformità SCA (CIS), **in
 * tempo reale**. Come «Network Security», interroga Wazuh al momento attraverso
 * la sonda (login JWT + letture). Vulnerabilità e allarmi arrivano in una fase
 * successiva, dall'Indexer.
 */
/**
 * La sezione «Protezione dati»: la copertura Veeam del CI, cercata in tempo reale
 * via sonda e abbinata per nome. Dice se la VM/server è protetta, da quali
 * oggetti di backup e con quanti punti di ripristino. Nulla di salvato.
 */
/**
 * Che cosa dice phpIPAM degli indirizzi di questo CI: descrizione, nome,
 * proprietario, stato. Un CI con indirizzi e nessun record è «in uso ma non
 * documentato», ed è la cosa da dire per prima.
 */
function SezioneIpam({ ciId, puoScrivere }: { ciId: string; puoScrivere: boolean }) {
  const dati = useAsync(() => api.ciIpam(ciId), [ciId])
  const [erroreDoc, setErroreDoc] = useState<string | null>(null)
  const [documentando, setDocumentando] = useState<string | null>(null)
  const d = dati.data
  const trovati = d?.addresses ?? []
  const documentati = new Set(trovati.map((a) => a.ip))
  const mancanti = (d?.ips ?? []).filter((ip) => !documentati.has(ip))

  async function documenta(ip: string) {
    setErroreDoc(null)
    setDocumentando(ip)
    try {
      await api.ipamDocumentAddress(ip)
      await dati.reload()
    } catch (e) {
      setErroreDoc(e instanceof Error ? e.message : String(e))
    } finally {
      setDocumentando(null)
    }
  }

  return (
    <Card
      title="IPAM (phpIPAM)"
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Interrogo…') : t('Aggiorna')}
        </button>
      }
    >
      <p className="field__hint">
        {t('Che cosa è documentato in phpIPAM per gli indirizzi di questo oggetto, chiesto al momento.')}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason}</p>
      ) : (
        <>
          {mancanti.length > 0 && (
            <div className="banner banner--warn">
              <p style={{ margin: 0 }}>{t('In uso ma non documentati in phpIPAM:')}</p>
              <ul className="lista-semplice" style={{ marginTop: 4 }}>
                {mancanti.map((ip) => (
                  <li key={ip} style={{ display: 'flex', gap: 8, alignItems: 'center', border: 'none', padding: '2px 0' }}>
                    <span className="mono">{ip}</span>
                    {puoScrivere && (
                      <button className="link" disabled={documentando === ip}
                        onClick={() => documenta(ip)}>
                        {documentando === ip ? t('Documento…') : t('Documenta in phpIPAM')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {erroreDoc && <p className="field__hint" style={{ color: 'var(--error)' }}>{erroreDoc}</p>}
            </div>
          )}
          {trovati.length === 0 ? (
            <EmptyState
              title={t('Nessun indirizzo documentato')}
              hint={t('phpIPAM non conosce nessuno degli indirizzi di questo oggetto.')}
            />
          ) : (
            <DataTable
              rows={trovati}
              rowKey={(a) => a.ip}
              columns={[
                { key: 'ip', header: 'IP', mono: true, value: (a: IpamAddress) => a.ip },
                { key: 'hostname', header: t('Nome in phpIPAM'), value: (a) => a.hostname },
                { key: 'description', header: t('Descrizione'), value: (a) => a.description },
                { key: 'owner', header: t('Proprietario'), value: (a) => a.owner },
                { key: 'tag', header: t('Stato in phpIPAM'), value: (a) => a.tag },
                { key: 'last_seen', header: t('Ultima vista (phpIPAM)'), value: (a) => a.last_seen ?? '' },
              ]}
            />
          )}
        </>
      )}
    </Card>
  )
}

function SezioneProtezioneDati({ ciId, fornitori }: { ciId: string; fornitori: string[] }) {
  const etichette: Record<string, string> = { veeam: 'Veeam', vinchin: 'Vinchin' }
  const dati = useAsync(() => api.ciBackup(ciId), [ciId])
  const d = dati.data
  const matched = d?.matched ?? []
  const puntiMax = matched.reduce((m, o) => Math.max(m, o.restore_points ?? 0), 0)
  // Il backup più recente fra tutti gli oggetti abbinati (le date ISO si
  // ordinano come testo, quindi il massimo è il più recente).
  const ultimoBackup = matched.reduce<string | null>(
    (max, o) => (o.last_backup && (!max || o.last_backup > max) ? o.last_backup : max),
    null,
  )

  return (
    <Card
      title={t('Protezione dati')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Interrogo…') : t('Aggiorna')}
        </button>
      }
    >
      <p className="field__hint">
        {t('La copertura di backup di questo oggetto, chiesta al sistema di backup al momento tramite la sonda e abbinata per nome.')}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Backup non interrogabile.')}</p>
      ) : !d.protected ? (
        <EmptyState
          title={t('Nessun backup per questo oggetto')}
          hint={t('Nessun sistema di backup ha un oggetto che corrisponde al nome di questo CI: potrebbe non essere protetto, o avere nel backup un nome diverso.')}
        />
      ) : (
        <>
          <div className="stats" style={{ marginBottom: 12 }}>
            <StatCard label={t('Stato')} value={t('Protetto')} tone="ok" />
            <StatCard label={t('Ultimo backup')} value={ultimoBackup ? formatDateTime(ultimoBackup) : '—'} />
            <StatCard label={t('Oggetti di backup')} value={matched.length} />
            <StatCard label={t('Punti di ripristino')} value={puntiMax} />
          </div>
          <DataTable
            rows={matched}
            initialSort={{ key: 'name' }}
            rowKey={(o) => o.id}
            columns={[
              ...(fornitori.length > 1
                ? [{ key: 'vendor', header: t('Fornitore'), value: (o: VeeamObject) => (o.vendor ? etichette[o.vendor] ?? o.vendor : '') }]
                : []),
              { key: 'name', header: t('Oggetto'), value: (o: VeeamObject) => o.name ?? '' },
              { key: 'type', header: t('Tipo'), value: (o) => o.type ?? '' },
              { key: 'platform', header: t('Piattaforma'), value: (o) => o.platform ?? '', render: (o) => o.platform ?? '—' },
              {
                key: 'last_backup', header: t('Ultimo backup'),
                value: (o) => o.last_backup ?? '',
                render: (o) => (o.last_backup ? formatDateTime(o.last_backup) : '—'),
              },
              {
                key: 'restore_points', header: t('Punti di ripristino'),
                value: (o) => o.restore_points ?? 0,
                render: (o) => (o.restore_points == null ? '—' : String(o.restore_points)),
              },
            ]}
          />
        </>
      )}
    </Card>
  )
}

function SezioneWazuh({ ciId }: { ciId: string }) {
  const dati = useAsync(() => api.ciWazuh(ciId), [ciId])
  const d = dati.data

  return (
    <Card
      title={t('Wazuh')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Interrogo…') : t('Aggiorna')}
        </button>
      }
    >
      <p className="field__hint">
        {t('Stato dell’agente e conformità, chiesti a Wazuh al momento tramite la sonda.')}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Wazuh non è interrogabile.')}</p>
      ) : !d.found ? (
        <EmptyState
          title={t('Nessun agente Wazuh su questo host')}
          hint={t('Wazuh non ha un agente per questo host, né allarmi da log inviati via syslog a suo nome.')}
        />
      ) : d.agentless ? (
        <>
          <div className="banner banner--info">
            {t('Questo apparato non ha un agente Wazuh, ma invia i suoi log a Wazuh (syslog): qui sotto gli allarmi generati da quei log. Conformità (SCA) e vulnerabilità richiedono un agente installato e non sono disponibili.')}
          </div>
          {d.last_log_at && (
            <p className="field__hint">
              {t('Ultimo log ricevuto da Wazuh: {quando}', { quando: formatDateTime(d.last_log_at) })}
            </p>
          )}
          <h4 style={{ margin: '14px 0 6px', fontSize: 14 }}>
            {t('Allarmi rilevanti')} ({d.alerts?.length ?? 0})
          </h4>
          {(d.alerts?.length ?? 0) === 0 ? (
            <p className="muted">{t('Nessun allarme di livello ≥ 7 negli ultimi 7 giorni.')}</p>
          ) : (
            <DataTable
              columns={colonneAllarmiWazuh()}
              rows={d.alerts!}
              rowKey={(a) => a.rule_id}
              conteggio={{ nome: t('allarmi') }}
            />
          )}
          {d.queried_at && (
            <p className="field__hint">
              {t('Interrogato: {quando}', { quando: formatDateTime(d.queried_at) })}
            </p>
          )}
        </>
      ) : (
        <>
          <div className="ci-sommario">
            <span className="ci-sommario__item">
              <span className="ci-sommario__k">{t('Agente')}</span>
              <StatoAgenteWazuh stato={d.agent?.status} />
            </span>
            {d.agent?.version && (
              <span className="ci-sommario__item">
                <span className="ci-sommario__k">{t('Versione')}</span>{d.agent.version}
              </span>
            )}
            {d.agent?.os && (
              <span className="ci-sommario__item">
                <span className="ci-sommario__k">{t('Sistema operativo')}</span>{d.agent.os}
              </span>
            )}
            {d.agent?.last_keep_alive && (
              <span className="ci-sommario__item">
                <span className="ci-sommario__k">{t('Ultimo keep-alive')}</span>
                {formatDateTime(d.agent.last_keep_alive)}
              </span>
            )}
          </div>

          <h4 style={{ margin: '14px 0 6px', fontSize: 14 }}>
            {t('Vulnerabilità')} ({d.vulnerabilities?.length ?? 0})
          </h4>
          {!d.alerts_available ? (
            <p className="muted">
              {t('Allarmi e vulnerabilità richiedono l’Indexer di Wazuh, non configurato in questa credenziale.')}
            </p>
          ) : (d.vulnerabilities?.length ?? 0) === 0 ? (
            <p className="muted">{t('Nessuna vulnerabilità rilevata da Wazuh su questo host.')}</p>
          ) : (
            <>
            <Donut centreLabel={t('CVE')} slices={fetteGravitaVuln(d.vulnerabilities!)} />
            <DataTable
              columns={colonneVulnWazuh()}
              rows={d.vulnerabilities!}
              rowKey={(v, i) => `${v.cve}-${i}`}
              conteggio={{ nome: t('vulnerabilità') }}
            />
            </>
          )}

          <h4 style={{ margin: '14px 0 6px', fontSize: 14 }}>
            {t('Conformità (SCA / CIS)')} ({d.sca?.length ?? 0})
          </h4>
          {(d.sca?.length ?? 0) === 0 ? (
            <p className="muted">{t('Nessuna policy SCA valutata su questo agente.')}</p>
          ) : (
            <DataTable
              columns={colonneScaWazuh()}
              rows={d.sca!}
              rowKey={(p) => p.policy_id}
            />
          )}

          <h4 style={{ margin: '14px 0 6px', fontSize: 14 }}>
            {t('Allarmi rilevanti')} ({d.alerts?.length ?? 0})
          </h4>
          {!d.alerts_available ? (
            <p className="muted">
              {t('Allarmi e vulnerabilità richiedono l’Indexer di Wazuh, non configurato in questa credenziale.')}
            </p>
          ) : (d.alerts?.length ?? 0) === 0 ? (
            <p className="muted">{t('Nessun allarme di livello ≥ 7 negli ultimi 7 giorni.')}</p>
          ) : (
            <DataTable
              columns={colonneAllarmiWazuh()}
              rows={d.alerts!}
              rowKey={(a) => a.rule_id}
              conteggio={{ nome: t('allarmi') }}
            />
          )}

          {d.queried_at && (
            <p className="field__hint">
              {t('Interrogato: {quando}', { quando: formatDateTime(d.queried_at) })}
            </p>
          )}
        </>
      )}
    </Card>
  )
}

// ------------------------------------------- Firewall: traffico (Palo Alto/FortiGate) + bloccato (Wazuh)

/**
 * La sezione «Firewall» del CI, con fino a due sorgenti complementari:
 * - il **traffico** letto direttamente dal firewall, Palo Alto o FortiGate (completo, con
 *   «solo bloccato» e la scelta del firewall quando ce n'è più d'uno);
 * - il **bloccato** aggregato per peer, letto dai log del firewall già in Wazuh.
 */
function SezioneFirewall({
  ciId,
  haPaloAlto,
  haWazuh,
}: {
  ciId: string
  haPaloAlto: boolean
  haWazuh: boolean
}) {
  return (
    <>
      {haPaloAlto && <FirewallTrafficoLive ciId={ciId} />}
      {haWazuh && <FirewallBloccatiWazuh ciId={ciId} />}
    </>
  )
}

/**
 * Il traffico dell'host chiesto **direttamente al firewall** Palo Alto o FortiGate (via
 * sonda): gli ultimi flussi visti, con l'interruttore «solo bloccato» e — se il
 * cliente ha più firewall — la scelta di quale interrogare.
 */
function FirewallTrafficoLive({ ciId }: { ciId: string }) {
  const [soloBloccato, setSoloBloccato] = useState(false)
  const [firewallId, setFirewallId] = useState<string | undefined>(undefined)
  const dati = useAsync(
    () => api.ciFirewallTraffic(ciId, { blocked: soloBloccato, firewallId }),
    [ciId, soloBloccato, firewallId],
  )
  const d = dati.data
  const firewalls = d?.firewalls ?? []
  const piuFirewall = firewalls.length > 1

  return (
    <Card
      title={t('Traffico dal firewall')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Interrogo…') : t('Aggiorna')}
        </button>
      }
    >
      <p className="field__hint">
        {t('Ultimo traffico che il firewall ha visto da/verso questo host, letto in tempo reale via sonda.')}
      </p>
      <div className="ci-sommario" style={{ gap: 16, alignItems: 'center' }}>
        {piuFirewall && (
          <label className="field" style={{ margin: 0 }}>
            <span className="field__label">{t('Firewall')}</span>
            <select
              value={firewallId ?? ''}
              onChange={(e) => setFirewallId(e.target.value || undefined)}
            >
              <option value="">{t('Scegli un firewall…')}</option>
              {firewalls.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="checkbox" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={soloBloccato}
            onChange={(e) => setSoloBloccato(e.target.checked)}
          />
          {t('Solo bloccato')}
        </label>
      </div>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Firewall non interrogabile.')}</p>
      ) : d.needs_choice ? (
        <EmptyState
          title={t('Scegli quale firewall')}
          hint={t('Sono configurati più firewall: scegline uno qui sopra per vedere il traffico di questo host.')}
        />
      ) : !d.found ? (
        <EmptyState
          title={soloBloccato ? t('Niente bloccato dal firewall') : t('Nessun traffico sul firewall')}
          hint={t('Il firewall non ha righe recenti per gli indirizzi di questo host.')}
        />
      ) : (
        <>
          <FirewallFlowsTable flows={d.flows ?? []} />
          {d.queried_at && (
            <p className="field__hint">
              {t('Interrogato: {quando}', { quando: formatDateTime(d.queried_at) })}
            </p>
          )}
        </>
      )}
    </Card>
  )
}

/**
 * Cosa il firewall ha **bloccato** da/verso questo host, letto dai log (Palo
 * Alto) già in Wazuh. In ingresso (chi è stato fermato mentre lo raggiungeva) e
 * in uscita (dove l'host è stato fermato), aggregato per peer.
 */
function FirewallBloccatiWazuh({ ciId }: { ciId: string }) {
  const dati = useAsync(() => api.ciFirewall(ciId), [ciId])
  const d = dati.data

  return (
    <Card
      title={t('Bloccato dal firewall')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Interrogo…') : t('Aggiorna')}
        </button>
      }
    >
      <p className="field__hint">
        {t('Traffico che il firewall ha bloccato da/verso questo host, dai log in Wazuh (ultimi 7 giorni).')}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : !d.available ? (
        <p className="muted">{d.reason ?? t('Firewall non interrogabile.')}</p>
      ) : !d.found ? (
        <EmptyState
          title={t('Niente bloccato dal firewall')}
          hint={t('Nessun traffico da/verso questo host risulta bloccato dal firewall negli ultimi 7 giorni.')}
        />
      ) : (
        <>
          <div className="ci-sommario">
            <span className="ci-sommario__item">
              <span className="ci-sommario__k">{t('Bloccati (7g)')}</span>{d.total ?? 0}
            </span>
            {(d.by_action ?? []).map((a) => (
              <span key={a.action} className="badge badge--warn">
                {a.action} ×{a.count ?? 0}
              </span>
            ))}
          </div>
          <TabellaFirewall titolo={t('Bloccato in ingresso')} colonnaPeer={t('Sorgente')} righe={d.inbound ?? []} />
          <TabellaFirewall titolo={t('Bloccato in uscita')} colonnaPeer={t('Destinazione')} righe={d.outbound ?? []} />
          {d.queried_at && (
            <p className="field__hint">
              {t('Interrogato: {quando}', { quando: formatDateTime(d.queried_at) })}
            </p>
          )}
        </>
      )}
    </Card>
  )
}

function TabellaFirewall({
  titolo,
  colonnaPeer,
  righe,
}: {
  titolo: string
  colonnaPeer: string
  righe: FirewallPeer[]
}) {
  if (!righe.length) return null
  return (
    <>
      <h4 style={{ margin: '14px 0 6px', fontSize: 14 }}>
        {titolo} ({righe.length})
      </h4>
      <DataTable
        rows={righe}
        initialSort={{ key: 'count', dir: 'desc' }}
        rowKey={(r, i) => `${r.peer}-${i}`}
        columns={[
          { key: 'peer', header: colonnaPeer, mono: true, value: (r) => r.peer },
          { key: 'port', header: t('Porta'), mono: true, value: (r) => r.port ?? '' },
          { key: 'application', header: t('Applicazione'), value: (r) => r.application ?? '' },
          { key: 'rule', header: t('Regola'), value: (r) => r.rule ?? '' },
          {
            key: 'count', header: t('Bloccati'), value: (r) => r.count ?? 0,
            render: (r) => (r.count == null ? '—' : String(r.count)),
          },
        ]}
      />
    </>
  )
}

// ------------------------------------------- Routing (tabella L3 via SNMP)

/**
 * La tabella di routing L3 dell'apparato, raccolta via SNMP (opzione
 * «routing_table»): destinazione, next hop, interfaccia, protocollo, tipo,
 * metrica. Colonne filtrabili e ordinabili — su un router l'elenco è lungo.
 */
/** Colori della banda radio, per banda e non per posizione: la stessa banda ha
 *  sempre la stessa tinta, qualunque sia il mix di client del momento. Terna
 *  validata per la visione dei colori su tema chiaro e scuro. */
const COLORE_BANDA: Record<string, string> = {
  '2.4G': '#d97706',
  '5G': '#3b82f6',
  '6G': '#16a34a',
}

/** Le classi di qualità del segnale, dal RSSI (dBm) dei client. Le soglie sono
 *  quelle d'uso comune nel Wi-Fi: sopra −55 si lavora bene, sotto −75 no. È uno
 *  **stato**, quindi porta i colori di stato — non quelli delle categorie. */
const CLASSI_SEGNALE = [
  { key: 'ottimo', min: -55, color: 'var(--ok)' },
  { key: 'buono', min: -65, color: 'var(--info)' },
  { key: 'debole', min: -75, color: 'var(--warn)' },
  { key: 'critico', min: -Infinity, color: 'var(--error)' },
] as const

/** L'etichetta tradotta di una classe di segnale (literal dentro t(), come
 *  vuole la guardia delle traduzioni). */
function etichettaSegnale(chiave: string): string {
  switch (chiave) {
    case 'ottimo':
      return t('ottimo')
    case 'buono':
      return t('buono')
    case 'debole':
      return t('debole')
    default:
      return t('critico')
  }
}

/** Le fette «client per banda», nell'ordine fisso delle bande. */
function fetteBanda(clients: WlanClientEntry[]): DonutSlice[] {
  const conta = new Map<string, number>()
  for (const c of clients) {
    const banda = (c.band ?? '').trim()
    if (banda) conta.set(banda, (conta.get(banda) ?? 0) + 1)
  }
  const note = Object.keys(COLORE_BANDA)
  const fette: DonutSlice[] = note
    .filter((b) => conta.has(b))
    .map((b) => ({ key: b, label: b, value: conta.get(b)!, color: COLORE_BANDA[b] }))
  // Una banda che non conosciamo (un firmware che scrive diverso) si mostra
  // comunque, in tinta neutra: sparire sarebbe peggio che essere grigia.
  for (const [banda, n] of conta) {
    if (!note.includes(banda)) {
      fette.push({ key: banda, label: banda, value: n, color: 'var(--text-muted)' })
    }
  }
  return fette
}

/** Le fette «qualità del segnale», dai RSSI dei client. Chi non ha un RSSI (un
 *  client in autenticazione) finisce in «sconosciuto», dichiarato. */
function fetteSegnale(clients: WlanClientEntry[]): DonutSlice[] {
  const conta = new Map<string, number>()
  let sconosciuti = 0
  for (const c of clients) {
    const rssi = Number.parseInt(c.rssi ?? '', 10)
    if (Number.isNaN(rssi)) {
      sconosciuti += 1
      continue
    }
    const classe = CLASSI_SEGNALE.find((q) => rssi >= q.min) ?? CLASSI_SEGNALE[3]
    conta.set(classe.key, (conta.get(classe.key) ?? 0) + 1)
  }
  const fette: DonutSlice[] = CLASSI_SEGNALE.filter((q) => conta.has(q.key)).map((q) => ({
    key: q.key,
    label: etichettaSegnale(q.key),
    value: conta.get(q.key)!,
    color: q.color,
  }))
  if (sconosciuti > 0) {
    fette.push({
      key: 'sconosciuto', label: t('sconosciuto'),
      value: sconosciuti, color: 'var(--text-muted)',
    })
  }
  return fette
}

/** Conta i client per un campo (SSID, access point), per le barre. */
function clientiPer(clients: WlanClientEntry[], campo: (c: WlanClientEntry) => string): Category[] {
  const conta = new Map<string, number>()
  for (const c of clients) {
    const chiave = campo(c).trim() || '—'
    conta.set(chiave, (conta.get(chiave) ?? 0) + 1)
  }
  return [...conta].map(([key, value]) => ({ key, label: key, value }))
}

/**
 * La WLAN di un controller wireless, dall'inventario wireless via SSH: gli
 * access point (CI a sé, legati al controller), gli SSID diffusi da ogni radio
 * e i client associati all'ultima lettura. Via CLI e non SNMP perché gli
 * AirEngine con YunShan OS la MIB WLAN non la espongono.
 */
function SezioneWireless({ ciId }: { ciId: string }) {
  const dati = useAsync(() => api.ciWlan(ciId), [ciId])
  const d = dati.data
  const ap = d?.access_points ?? []
  const vaps = d?.vaps ?? []
  const clients = d?.clients ?? []

  const colonneAp: Column<WlanAccessPoint>[] = [
    {
      key: 'name',
      header: t('Nome'),
      value: (r) => r.name ?? '',
      render: (r) => <Link to={`/cis/${r.id}`}>{r.name ?? r.mac ?? '—'}</Link>,
    },
    { key: 'model', header: t('Modello'), value: (r) => r.model ?? '' },
    { key: 'ip', header: t('Indirizzo'), mono: true, value: (r) => r.ip ?? '' },
    { key: 'mac', header: 'MAC', mono: true, value: (r) => r.mac ?? '' },
    { key: 'group', header: t('Gruppo'), value: (r) => r.group ?? '' },
    {
      key: 'state',
      header: t('Stato'),
      value: (r) => r.state ?? '',
      render: (r) =>
        r.state === 'nor' ? (
          <span className="badge badge--ok">{t('normale')}</span>
        ) : (
          <span className="badge">{r.state ?? '—'}</span>
        ),
    },
    { key: 'uptime', header: t('Acceso da'), value: (r) => r.uptime ?? '' },
  ]
  const colonneVap: Column<WlanVap>[] = [
    { key: 'ssid', header: 'SSID', value: (r) => r.ssid },
    { key: 'ap', header: 'AP', value: (r) => r.ap_name ?? '' },
    { key: 'radio', header: t('Radio'), value: (r) => r.radio ?? '' },
    { key: 'bssid', header: 'BSSID', mono: true, value: (r) => r.bssid ?? '' },
    { key: 'auth', header: t('Autenticazione'), value: (r) => r.auth ?? '' },
    { key: 'status', header: t('Stato'), value: (r) => r.status ?? '' },
    {
      key: 'sta',
      header: t('Client'),
      value: (r) => r.sta_count ?? 0,
      render: (r) => (r.sta_count == null ? '—' : String(r.sta_count)),
    },
  ]
  const colonneClient: Column<WlanClientEntry>[] = [
    {
      key: 'mac',
      header: 'MAC',
      mono: true,
      value: (r) => r.mac,
      // Il MAC porta al CI dell'endpoint quando in CMDB c'è: la riga volatile
      // diventa un oggetto navigabile.
      render: (r) =>
        r.client_ci_id ? <Link to={`/cis/${r.client_ci_id}`}>{r.mac}</Link> : r.mac,
    },
    {
      key: 'mac_vendor',
      header: t('Dispositivo'),
      value: (r) => (r.mac_random ? t('MAC casuale') : r.mac_vendor ?? ''),
      render: (r) =>
        r.mac_random ? (
          // Un MAC «locally administered» è quasi sempre la privacy di un
          // telefono: dirlo vale più che tacere un vendor che non esiste.
          <span className="badge" title={t('MAC randomizzato dal dispositivo (privacy)')}>
            {t('MAC casuale')}
          </span>
        ) : (
          r.mac_vendor ?? '—'
        ),
    },
    { key: 'hostname', header: t('Nome'), value: (r) => r.hostname ?? '' },
    { key: 'username', header: t('Utente'), value: (r) => r.username ?? '' },
    { key: 'ip', header: t('Indirizzo'), mono: true, value: (r) => r.ip ?? '' },
    { key: 'ssid', header: 'SSID', value: (r) => r.ssid ?? '' },
    { key: 'ap', header: 'AP', value: (r) => r.ap_name ?? '' },
    { key: 'band', header: t('Banda'), value: (r) => r.band ?? '' },
    { key: 'protocol', header: t('Protocollo'), value: (r) => r.protocol ?? '' },
    { key: 'rx_tx', header: 'Rx/Tx (Mbps)', mono: true, value: (r) => r.rx_tx ?? '' },
    { key: 'rssi', header: 'RSSI', mono: true, value: (r) => r.rssi ?? '' },
    { key: 'vlan', header: 'VLAN', mono: true, value: (r) => r.vlan ?? '' },
  ]

  const vuota = !ap.length && !vaps.length && !clients.length
  return (
    <Card
      title={t('Wireless')}
      actions={
        <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
          {dati.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      }
    >
      <p className="field__hint">
        {t(
          'Gli access point gestiti, gli SSID diffusi e i client associati: da un controller con l’inventario avanzato apparati (via SSH), o dall’access point stesso con il driver SNMP (Aerohive / Extreme). I client si contano anche fra i dispositivi di Traffic Analysis.',
        )}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : vuota ? (
        <EmptyState
          title={t('Nessun access point censito')}
          hint={t('Lancia una discovery «Inventario avanzato apparati» su questo controller.')}
        />
      ) : (
        <>
          <p className="field__hint">
            {t('{ap} access point, {ssid} SSID, {client} client', {
              ap: d.counts.access_points,
              ssid: d.counts.ssids,
              client: d.counts.clients,
            })}
          </p>
          {clients.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div>
                <h3>{t('Client per banda')}</h3>
                <Donut centreLabel={t('client')} slices={fetteBanda(clients)} />
              </div>
              <div>
                <h3>{t('Qualità del segnale')}</h3>
                <Donut centreLabel={t('client')} slices={fetteSegnale(clients)} />
                <p className="field__hint">
                  {t('Dal RSSI dei client: ottimo ≥ −55 dBm, buono ≥ −65, debole ≥ −75, critico sotto.')}
                </p>
              </div>
              <div>
                <h3>{t('Client per SSID')}</h3>
                <CategoryBars data={clientiPer(clients, (c) => c.ssid ?? '')} />
              </div>
              <div>
                <h3>{t('Client per access point')}</h3>
                <CategoryBars data={clientiPer(clients, (c) => c.ap_name ?? '')} />
              </div>
            </div>
          )}
          {ap.length > 0 && (
            <DataTable
              columns={colonneAp}
              rows={ap}
              initialSort={{ key: 'name' }}
              rowKey={(r) => r.id}
            />
          )}
          {vaps.length > 0 && (
            <>
              <h3>{t('Reti diffuse')}</h3>
              <DataTable
                columns={colonneVap}
                rows={vaps}
                initialSort={{ key: 'ssid' }}
                rowKey={(r, i) => `${r.ssid}-${r.bssid ?? ''}-${i}`}
              />
            </>
          )}
          {clients.length > 0 && (
            <>
              <h3>{t('Client connessi')}</h3>
              <DataTable
                columns={colonneClient}
                rows={clients}
                initialSort={{ key: 'ssid' }}
                rowKey={(r, i) => `${r.mac}-${i}`}
              />
            </>
          )}
        </>
      )}
    </Card>
  )
}

/**
 * Le ottiche di un firewall si leggono **via API**: PAN-OS le tiene nello
 * stato di sistema, FortiGate nella monitor API — nessuno dei due in SNMP,
 * dove ENTITY-MIB dà solo gli alimentatori. Lo stesso gesto della
 * sincronizzazione delle rotte, per la stessa ragione.
 */
/**
 * Quando nessun firewall corrisponde al CI (una coppia HA con la credenziale
 * sull'indirizzo condiviso, un CI senza seriale): il selettore con il perché
 * — cosa ha il CI, cosa sa ogni credenziale — e la scelta resta sul CI.
 */
function SceltaFirewall({ esito, scelto, onScelto }: {
  esito: FirewallSyncEsito
  scelto: string
  onScelto: (id: string) => void
}) {
  const why = esito.why
  const descrivi = (f: FirewallAbbinamento['firewalls'][number]) =>
    [f.name, [f.host, f.serial ? t('seriale {s}', { s: f.serial }) : null].filter(Boolean).join(', ')]
      .filter(Boolean)
      .join(' — ')
  const opzioni: { id: string; label: string }[] = why
    ? why.firewalls.map((f) => ({ id: f.id, label: descrivi(f) }))
    : (esito.firewalls ?? []).map((f) => ({ id: f.id, label: f.name }))
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span className="muted eagle-piccolo">
        {t('Nessun firewall corrisponde a questo CI: scegli quale.')}{' '}
        {why && (why.ci_serial
          ? t('Il CI ha seriale {s} e si presenta come {ids}.', { s: why.ci_serial, ids: why.ci_identifiers.join(', ') })
          : t('Il CI non ha un seriale in inventario e si presenta come {ids}.', { ids: why.ci_identifiers.join(', ') }))}
      </span>
      <select value={scelto} onChange={(e) => onScelto(e.target.value)} aria-label={t('Firewall')}>
        <option value="">{t('— scegli il firewall —')}</option>
        {opzioni.map((f) => (
          <option key={f.id} value={f.id}>{f.label}</option>
        ))}
      </select>
    </span>
  )
}

/**
 * Le VLAN di una porta in una cella: «20» per una porta d'accesso, «trunk
 * 1,10-20,30 · nativa 1» per un trunk. Il trunk si dice, perché «1,10-20» da
 * solo non dice se la porta le tagga o se è l'elenco di un accesso strano.
 */
function descriviVlan(i: NetworkInterface): string {
  if (!i.vlans && !i.pvid) return '—'
  if (i.vlan_mode === 'trunk') {
    const nativa = i.pvid ? ` · ${t('nativa {n}', { n: i.pvid })}` : ''
    return `${t('trunk')} ${i.vlans ?? ''}${nativa}`
  }
  return i.vlans || String(i.pvid)
}

function OtticheDalFirewall({ ciId, onFatto }: { ciId: string; onFatto: () => void }) {
  const [lavoro, setLavoro] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [scelta, setScelta] = useState<FirewallSyncEsito | null>(null)
  const [firewallId, setFirewallId] = useState('')
  async function leggi() {
    setLavoro(true)
    setMsg(null)
    try {
      const r = await api.ciFirewallTransceiversSync(ciId, firewallId || undefined)
      if (!r.available) setMsg(r.reason ?? t('Firewall non interrogabile.'))
      else if (r.needs_choice) setScelta(r)
      else {
        setScelta(null)
        setMsg(t('{n} ottiche lette dal firewall, {c} CI creati.', { n: r.count ?? 0, c: r.parts_created ?? 0 }))
        onFatto()
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setLavoro(false)
    }
  }
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {msg && <span className="muted eagle-piccolo">{msg}</span>}
      {scelta && <SceltaFirewall esito={scelta} scelto={firewallId} onScelto={setFirewallId} />}
      <button type="button" className="secondary" onClick={() => void leggi()} disabled={lavoro || (!!scelta && !firewallId)}>
        {lavoro ? t('Leggo…') : t('Leggi le ottiche dal firewall')}
      </button>
    </span>
  )
}

function SezioneRouting({ ciId, syncFirewall }: { ciId: string; syncFirewall?: boolean }) {
  const dati = useAsync(() => api.ciRoutes(ciId), [ciId])
  const d = dati.data
  // Si nascondono le rotte verso loopback (127/8) e broadcast/riservato (255/8):
  // rumore in tabella, non rotte utili.
  const righe = (d?.routes ?? []).filter((r) => !reteNascosta(r.dest))
  const [sincronizzando, setSincronizzando] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [errSync, setErrSync] = useState<unknown>(null)
  const [scelta, setScelta] = useState<FirewallSyncEsito | null>(null)
  const [firewallId, setFirewallId] = useState('')

  async function sincronizza() {
    setSincronizzando(true)
    setMsg(null)
    setErrSync(null)
    try {
      const r = await api.ciFirewallRoutesSync(ciId, firewallId || undefined)
      if (!r.available) setMsg(r.reason ?? t('Firewall non interrogabile.'))
      else if (r.needs_choice) setScelta(r)
      else {
        setScelta(null)
        setMsg(t('{n} rotte sincronizzate dal firewall.', { n: r.count ?? 0 }))
        dati.reload()
      }
    } catch (e) {
      setErrSync(e)
    } finally {
      setSincronizzando(false)
    }
  }
  const colonne: Column<RouteEntry>[] = [
    {
      key: 'dest',
      header: t('Destinazione'),
      mono: true,
      value: (r) => `${r.dest}/${r.prefix_len}`,
    },
    { key: 'next_hop', header: t('Next hop'), mono: true, value: (r) => r.next_hop ?? '' },
    {
      key: 'iface',
      header: t('Interfaccia'),
      value: (r) => r.if_name ?? (r.if_index != null ? String(r.if_index) : ''),
    },
    { key: 'proto', header: t('Protocollo'), value: (r) => r.proto ?? '' },
    {
      key: 'distance',
      header: t('Distance'),
      value: (r) => (r.distance == null ? '' : r.distance),
      render: (r) => (r.distance == null ? '—' : String(r.distance)),
    },
    {
      key: 'metric',
      header: t('Metrica'),
      value: (r) => (r.metric == null ? '' : r.metric),
      render: (r) => (r.metric == null ? '—' : String(r.metric)),
    },
  ]
  // La colonna VRF/vpn-instance compare solo se almeno una rotta ne ha una:
  // sulla gran parte degli apparati (tabella sola globale) sarebbe sempre vuota.
  if (righe.some((r) => r.vrf)) {
    colonne.push({
      key: 'vrf',
      header: t('VRF/VPN-INSTANCE'),
      value: (r) => r.vrf ?? '',
      render: (r) => r.vrf ?? t('globale'),
    })
  }

  return (
    <Card
      title={t('Routing')}
      actions={
        <div style={{ display: 'flex', gap: 6 }}>
          {syncFirewall && scelta && <SceltaFirewall esito={scelta} scelto={firewallId} onScelto={setFirewallId} />}
          {syncFirewall && (
            <button className="secondary" onClick={sincronizza} disabled={sincronizzando || (!!scelta && !firewallId)}>
              {sincronizzando ? t('Sincronizzo…') : t('Sincronizza dal firewall')}
            </button>
          )}
          <button className="secondary" onClick={() => dati.reload()} disabled={dati.loading}>
            {dati.loading ? t('Aggiorno…') : t('Aggiorna')}
          </button>
        </div>
      }
    >
      <p className="field__hint">
        {syncFirewall
          ? t('La tabella di routing dell’apparato. Dai firewall Palo Alto, FortiGate e WatchGuard si legge dal firewall con «Sincronizza dal firewall» (Palo Alto non la espone via SNMP).')
          : t('La tabella di routing L3 dell’asset viene acquisita via SNMP e popolata tramite una discovery SNMP con l’opzione «Tabelle di routing» abilitata.')}
      </p>
      {msg && <p className="field__hint">{msg}</p>}
      <ErrorBanner error={dati.error ?? errSync} />
      {dati.loading && !d ? (
        <Spinner />
      ) : !d ? null : !righe.length ? (
        <EmptyState
          title={t('Nessuna rotta raccolta')}
          hint={t('Lancia una discovery SNMP approfondita con l’opzione «Tabelle di routing» su questo apparato.')}
        />
      ) : (
        <>
          <p className="field__hint">{t('{n} rotte', { n: righe.length })}</p>
          <DataTable
            columns={colonne}
            rows={righe}
            initialSort={{ key: 'dest' }}
            rowKey={(r, i) => `${r.dest}/${r.prefix_len}-${i}`}
          />
        </>
      )}
    </Card>
  )
}

/** The columns of the Wazuh alerts (agent and agentless views share them). */
function colonneAllarmiWazuh(): Column<WazuhAlert>[] {
  return [
    {
      key: 'level', header: t('Livello'), type: 'number', value: (a) => a.level ?? '',
      render: (a) => (
        <span className={`badge${(a.level ?? 0) >= 10 ? ' badge--error' : ' badge--warn'}`}>
          {a.level ?? '—'}
        </span>
      ),
    },
    {
      key: 'rule', header: t('Regola'), value: (a) => `${a.description ?? a.rule_id} ${a.rule_id}`,
      render: (a) => (
        <>
          {a.description ?? a.rule_id}
          <div className="muted mono">{a.rule_id}</div>
        </>
      ),
    },
    { key: 'count', header: t('Occorrenze'), type: 'number', value: (a) => a.count ?? '' },
    {
      key: 'last_seen', header: t('Ultimo'), type: 'date', value: (a) => a.last_seen ?? '',
      render: (a) => <span title={a.last_seen ?? ''}>{a.last_seen ? formatDateTime(a.last_seen) : '—'}</span>,
    },
  ]
}

/** The columns of the vulnerabilities Wazuh found on the host. */
function colonneVulnWazuh(): Column<WazuhVuln>[] {
  return [
    { key: 'cve', header: 'CVE', mono: true, value: (v) => v.cve ?? '' },
    {
      key: 'severity', header: t('Gravità'), value: (v) => v.severity ?? '',
      render: (v) => <span className={`badge${_gravitaWazuh(v.severity)}`}>{v.severity ?? '—'}</span>,
    },
    {
      key: 'score', header: t('Punteggio'), type: 'number', value: (v) => v.score ?? '',
      render: (v) => (typeof v.score === 'number' ? v.score.toFixed(1) : '—'),
    },
    {
      key: 'package', header: t('Pacchetto'),
      value: (v) => (v.package ? `${v.package}${v.version ? ' ' + v.version : ''}` : ''),
      render: (v) => (
        <>
          {v.package ? `${v.package}${v.version ? ' ' + v.version : ''}` : '—'}
          {(v.packages_count ?? 1) > 1 && (
            <span className="muted"> {t('(+{n} pacchetti)', { n: (v.packages_count ?? 1) - 1 })}</span>
          )}
        </>
      ),
    },
  ]
}

/** The columns of the SCA (CIS) policies evaluated on the Wazuh agent. */
function colonneScaWazuh(): Column<WazuhSca>[] {
  return [
    { key: 'name', header: t('Policy'), value: (p) => p.name ?? p.policy_id },
    {
      key: 'score', header: t('Punteggio'), type: 'number', value: (p) => p.score ?? '',
      render: (p) => (typeof p.score === 'number' ? `${p.score}%` : '—'),
    },
    { key: 'pass', header: t('Superati'), type: 'number', value: (p) => p.pass ?? '' },
    {
      key: 'fail', header: t('Falliti'), type: 'number', value: (p) => p.fail ?? '',
      render: (p) => (typeof p.fail === 'number' && p.fail > 0
        ? <span className="badge badge--warn">{p.fail}</span>
        : (p.fail ?? '—')),
    },
    {
      key: 'end_scan', header: t('Ultima scansione'), type: 'date', value: (p) => p.end_scan ?? '',
      render: (p) => <span title={p.end_scan ?? ''}>{p.end_scan ? formatDateTime(p.end_scan) : '—'}</span>,
    },
  ]
}

function StatoAgenteWazuh({ stato }: { stato?: string | null }) {
  if (stato === 'active') return <span className="badge badge--ok">{t('attivo')}</span>
  if (stato === 'disconnected') return <span className="badge badge--warn">{t('disconnesso')}</span>
  if (stato === 'never_connected')
    return <span className="badge">{t('mai connesso')}</span>
  return <span className="badge">{stato ?? '—'}</span>
}

function _gravitaWazuh(sev?: string | null): string {
  const s = (sev ?? '').toLowerCase()
  if (s === 'critical' || s === 'high') return ' badge--error'
  if (s === 'medium') return ' badge--warn'
  return ''
}

/** Colore per gravità (donut): rosso→ambra→blu→grigio, mai verde. Vedi Wazuh.tsx. */
const COLORE_GRAVITA_WAZUH: Record<string, string> = {
  critical: 'var(--error)',
  high: 'var(--warn)',
  medium: 'var(--info)',
  low: 'var(--text-muted)',
}

function coloreGravitaWazuh(sev?: string | null): string {
  return COLORE_GRAVITA_WAZUH[(sev ?? '').trim().toLowerCase()] ?? 'var(--muted-bg)'
}

/** Le fette del donut «vulnerabilità per gravità» dell'host: una CVE per riga,
 *  contate per gravità. L'ordine è quello di gravità, non di conteggio. */
function fetteGravitaVuln(vulns: { severity?: string | null }[]): DonutSlice[] {
  const ordine = ['Critical', 'High', 'Medium', 'Low']
  const conta = new Map<string, number>()
  for (const v of vulns) {
    const s = (v.severity ?? '—').trim() || '—'
    conta.set(s, (conta.get(s) ?? 0) + 1)
  }
  const chiavi = [...conta.keys()].sort((a, b) => {
    const ia = ordine.indexOf(a)
    const ib = ordine.indexOf(b)
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
  })
  return chiavi.map((sev) => ({
    key: sev,
    label: sev,
    value: conta.get(sev) ?? 0,
    color: coloreGravitaWazuh(sev),
  }))
}
